/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import * as React from 'react';
import { useTimelineActions } from './hooks/useTimelineActions';
import { normalizeVideoSettings } from './types/videoSettings';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AIEditorModal from './components/AIEditorModal';
import ApiKeyDialog from './components/ApiKeyDialog';
import CharacterManagerModal from './components/CharacterManager';
import DogmaManagerModal from './components/DogmaManager';
import ErrorBoundary from './components/ErrorBoundary';
import {
  BookMarkedIcon,
  CurvedArrowDownIcon,
  FilmIcon,
  FolderIcon,
  KeyIcon,
  SparklesIcon,
  UsersIcon,
  UploadCloudIcon,
} from './components/icons';
import { ProjectManager } from './components/ProjectManager';
import { ProjectWelcomeModal } from './components/ProjectWelcomeModal';
import { FileMenuBar } from './components/FileMenuBar';
import LoadingIndicator from './components/LoadingIndicator';
import PromptEditorModal from './components/PromptEditorModal';
import { VideoSettingsPanel } from './components/VideoSettingsPanel';
import PromptSequenceAssistant from './components/PromptSequenceAssistant';
import SequenceManager from './components/SequenceManager';
import ShotLibrary from './components/ShotLibrary';
import StoryboardPreviewModal from './components/StoryboardPreviewModal';
import ShotsTable from './components/ShotsTable';
import ContactSheet from './components/ContactSheet';
import { DecoupagePanel } from './components/DecoupagePanel';
import { DecoupageShot } from './types/decoupage-pro';

import { UserProfileModal } from './components/UserProfileModal'; // New
import { SessionHistoryModal } from './components/SessionHistoryModal'; // New
import { ExportDialog } from './components/ExportDialog';
import { BinManager } from './components/BinManager';
import { SourceViewer } from './components/SourceViewer';
import { TimelinePreview } from './components/TimelinePreview';
import { OnboardingGuide } from './components/OnboardingGuide';
import VideoResult from './components/VideoResult';
import VisualContextViewer from './components/VisualContextViewer';
import { ThemeSwitcher } from './components/ThemeSwitcher';
import { StudioLayout } from './components/StudioLayout';
import { FalJobInspector } from './components/FalJobInspector';
import { StudioSidebar } from './components/StudioSidebar';
import { StudioHeader } from './components/StudioHeader';

import ModeSelectionStep from './components/ModeSelectionStep';
import { useTheme } from './contexts/ThemeContext';
import useLocalStorage from './hooks/useLocalStorage';
import { useShotLibrary } from './hooks/useShotLibrary';
import { useProject } from './hooks/useProject';
import { useCharacterLibrary } from './hooks/useCharacterLibrary';
import { useSequenceBins } from './hooks/useSequenceBins';
import { useLocalMediaLibrary } from './hooks/useLocalMediaLibrary';
import { useSessionPersistence } from './hooks/useSessionPersistence'; // New // New Hook
import { useStudioHotkeys } from './hooks/useStudioHotkeys';
import { useStudioModals } from './hooks/useStudioModals';
import { useStudioState } from './hooks/useStudioState';
import { useTimelineState } from './hooks/useTimelineState';
import { CreateSequenceBinInput, SlotVideo } from './types/bins';
import { timelineStateToOTIO } from './utils/otioConverter';
import { ProjectProvider } from './contexts/ProjectContext';
import {
  generateVideo,
  reviseFollowingPrompts,
  fetchGeminiConfig,
  ApiError,
  uploadToGoogleFiles,
  getRuntimeApiKey, // New
  setRuntimeApiKey, // New
} from './services/geminiService';
import { generatePreview as generateNanoPreview } from './services/nanoService';
import { LTXService } from './services/ltxService';
import { ProviderKeys, getProviderKey } from './utils/providerKeys';
import { useAuth } from './contexts/AuthContext';
import { supabase } from './services/supabaseClient';
import {
  AppState,
  AppStage,
  AspectRatio,
  Character,
  Dogma,
  GenerateVideoParams,
  GenerationMode,
  ImageFile,
  NanoApplyPayload,
  NanoEditorContext,
  PromptSequence,
  PromptSequenceStatus,
  Resolution,
  SavedShot,
  SequenceProgress,
  SequenceVideoData,
  StoryboardPreview,
  VeoModel,
  VideoFile,
  VideoProvider,
} from './types';
import { ImageModelProvider } from './services/imageGenerationService';

import {
  Track,
  TimelineState,
  SegmentWithUI,
  SegmentRevision,
  FPS,
  DEFAULT_FPS
} from './types/timeline';
import VerticalTimelineStack from './components/VerticalTimelineStack';
import SegmentIAPanel from './components/SegmentIAPanel';
import { TimelineService } from './services/timelineService';
import HorizontalTimeline from './components/HorizontalTimeline';
import { usePlaybackEngine } from './services/playbackEngine';

// === DECOUPAGE PRO IMPORTS ===
import { ShotSpec, createEmptyShot, getNextTcIn, tcDurationSec, ensureShotIds, rippleShots, composeShotPrompt } from './types/decoupage';
import { DECOUPAGE_TEMPLATES, createShotsFromTemplate, exportProjectToJson, importProjectFromJson } from './types/templates';
import { generateShotCandidates } from './services/imageGenerators';
import { base64ToFile } from './utils/base64';
// === DRIVE STORAGE ===
import { isDriveConnected, uploadToDrive, uploadBlobToDrive } from './services/googleDriveClient';
import { generateThumbnail } from './utils/videoUtils';

// ===================================================================
// NEUTRAL DEFAULT: No hardcoded dogmas
// Dogma templates are available in data/dogmaTemplates.ts for optional import
// ===================================================================


// Imported components
import { PromptConception } from './components/PromptConception';
import { InfiniteChainPanel, ChainSegment } from './components/InfiniteChainPanel';
import VideoFrameSelectorModal from './components/VideoFrameSelectorModal';

const Studio: React.FC = () => {
  // === THEME ===
  const { theme, themeId } = useTheme();

  // === CENTRALIZED STATE HOOKS ===
  const studioState = useStudioState();
  const timelineHook = useTimelineState();

  // Destructure studio state (replaces ~50 useState calls)
  const {
    // Core App State
    appState, setAppState,
    currentStage, setCurrentStage,
    activeTab, setActiveTab,
    resetKey, setResetKey,
    onboardingKey, setOnboardingKey,
    activeSettingsTab, setActiveSettingsTab,
    videoUrl, setVideoUrl,
    errorMessage, setErrorMessage,
    showStatusMessage, setShowStatusMessage,
    // Dogma State
    dogmas, setDogmas,
    activeDogmaId, setActiveDogmaId,
    activeDogma,
    sequenceBoundDogma, setSequenceBoundDogma,
    pendingDogma, setPendingDogma,
    isAssistantBusy, setIsAssistantBusy,
    // Character Library (from re-exported hook)
    characters,
    addCharacter: handleAddCharacter,
    updateCharacter: handleUpdateCharacter,
    deleteCharacter: handleDeleteCharacter,
    // Folder Library (re-exported)
    folders,
    createFolder,
    deleteFolder: handleDeleteFolder,
    // Dogma Folders
    dogmaFolders,
    createDogmaFolder,
    deleteDogmaFolder,
  } = studioState;

  // === MODAL STATE MANAGEMENT ===
  const {
    showStudioConfig, setShowStudioConfig,
    isExportDialogOpen, setIsExportDialogOpen,
    showThemeModal, setShowThemeModal,
    showApiKeyDialog, setShowApiKeyDialog,
    isDogmaManagerOpen, setIsDogmaManagerOpen,
    isShotLibraryOpen, setIsShotLibraryOpen,
    isProjectManagerOpen, setIsProjectManagerOpen,
    isCharacterManagerOpen, setIsCharacterManagerOpen,
    isProfileModalOpen, setIsProfileModalOpen,
    isHistoryModalOpen, setIsHistoryModalOpen,
    isContactSheetOpen, setIsContactSheetOpen,
    storyboardModalContext, setStoryboardModalContext
  } = useStudioModals();

  // Handlers
  const handleActivateDogmaBuffered = useCallback((id: string | null) => {
    if (isAssistantBusy) {
      if (id) {
        const dogma = dogmas.find(d => d.id === id);
        setPendingDogma(dogma || null);
        console.log('[MultiTask] Dogma change buffered, will apply when idle');
        setShowStatusMessage('⏳ Dogma sera appliqué après génération');
        setTimeout(() => setShowStatusMessage(null), 2000);
      } else {
        setPendingDogma(null);
      }
    } else {
      // Apply immediately
      setActiveDogmaId(id);
    }
  }, [isAssistantBusy, dogmas, setActiveDogmaId]);

  const handleAssistantBusyChange = useCallback((busy: boolean) => {
    setIsAssistantBusy(busy);

    // When assistant becomes idle, apply pending dogma changes
    if (!busy && pendingDogma) {
      console.log('[MultiTask] Applying pending Dogma:', pendingDogma.title);
      setActiveDogmaId(pendingDogma.id);
      setPendingDogma(null);
      setShowStatusMessage(`🎬 Dogma "${pendingDogma.title}" appliqué`);
      setTimeout(() => setShowStatusMessage(null), 2500);
    }
  }, [pendingDogma, setActiveDogmaId]);

  const handleConfigUpdate = useCallback((config: GenerateVideoParams) => {
    setLastConfig(prev => (prev ? { ...prev, ...config } : config));
  }, []);

  // Helper for selecting dogma
  const handleSelectDogma = useCallback((dogma: Dogma) => {
    handleActivateDogmaBuffered(dogma.id);
    setIsDogmaManagerOpen(false);
  }, [handleActivateDogmaBuffered, setIsDogmaManagerOpen]);

  // === VIDEO & API STATE (from studioState) ===
  const {
    lastConfig, setLastConfig,
    lastVideoObject, setLastVideoObject,
    isMediaSaving, setIsMediaSaving,
    lastVideoBlob, setLastVideoBlob,
    apiKey, setApiKey,
    ltxApiKey, setLtxApiKey,
    hasCustomKey, setHasCustomKey,
    isCurrentVideoSaved, setIsCurrentVideoSaved,
    frameToEdit, setFrameToEdit,
    // Sequence State
    promptSequence, setPromptSequence,
    activePromptIndex, setActivePromptIndex,
    sequenceProgress, setSequenceProgress,
    isRevisingSequence, setIsRevisingSequence,
    sequenceVideoData, setSequenceVideoData,
    mainPromptConfig, setMainPromptConfig,
    sortedSequenceHistory,
    // Nano
    nanoEditorContext, setNanoEditorContext,
    storyboardByIndex, setStoryboardByIndex,
    // P0.4: Active start/end image tracking
    activeStartImageIndex, setActiveStartImageIndex,
    activeEndImageIndex, setActiveEndImageIndex,
    // Assistant
    originalVideoForExtension, setOriginalVideoForExtension,
    isExternalVideoSource, setIsExternalVideoSource,
    assistantExtensionContext, setAssistantExtensionContext,
    assistantImage, setAssistantImage,
    assistantReferenceVideo, setAssistantReferenceVideo,
    assistantMotionDescription, setAssistantMotionDescription,
    characterToInject, setCharacterToInject,
    mentionedCharacters, setMentionedCharacters,
    assistantMessages, setAssistantMessages,
    // Editing
    editingPromptDetails, setEditingPromptDetails,
    // Découpage
    decoupageShots, setDecoupageShots,
    decoupageFps, setDecoupageFps,
    selectedDecoupageShot, setSelectedDecoupageShot,
    candidatesByShotId, setCandidatesByShotId,
    shotCandidates, setShotCandidates,
    isGeneratingCandidates, setIsGeneratingCandidates,
    selectedCandidateIndex, setSelectedCandidateIndex,
    rippleEnabled, setRippleEnabled,
    decoupageMasterShot, setDecoupageMasterShot,
    // Keyframes
    autoKeyframesEnabled, setAutoKeyframesEnabled,
    isGeneratingKeyframes, setIsGeneratingKeyframes,
    generatingKeyframeIndex, setGeneratingKeyframeIndex,
    // Mode
    sequenceMode, setSequenceMode,
    // Infinite Chain
    chainSegments, setChainSegments,
    isBatchGeneratingChain, setIsBatchGeneratingChain,
    // Provider Selection
    selectedImageProvider, setSelectedImageProvider,
    videoSettings, setVideoSettings,
    // NLE
    sourceMedia, setSourceMedia,
    // Session History
    historySessions, setHistorySessions,
    isLoadingHistory, setIsLoadingHistory,
    // Form State
    initialFormValues, setInitialFormValues,
    apiKeyError, setApiKeyError,
    // Refs
    abortControllerRef,
    // Shot Library (from re-exported hook)
    shots: savedShots,
    addShot: handleSaveShotToLibrary,
    deleteShot: handleDeleteShot,
    updateShotTitle: handleUpdateShotTitle,
    // Project (from re-exported hook)
    project: activeProject,
    updateProject,
    // Sequence Bins (from re-exported hook)
    bins,
    createBin,
    fillSlotWithVideo,
    setSlotStatus,
    getBinById,
    getSlotById,
  } = studioState;

  // Update hasCustomKey when apiKey changes
  useEffect(() => {
    setHasCustomKey(!!apiKey);
  }, [apiKey, setHasCustomKey]);

  // Ensure all shots have IDs (auto-fix for imported/LLM shots)
  useEffect(() => {
    if (decoupageShots.length > 0) {
      const fixedShots = ensureShotIds(decoupageShots);
      // Only update if changes were made to avoid loops (ensureShotIds creates new references only if meaningful changes)
      const hasChanges = fixedShots.some((s, i) => s.id !== decoupageShots[i].id);
      if (hasChanges) {
        setDecoupageShots(fixedShots);
      }
    }
  }, [decoupageShots, setDecoupageShots]);

  // === LOCAL MEDIA LIBRARY (Rushes & Uploads) ===
  const { uploadMedia } = useLocalMediaLibrary();

  // === CHAIN FRAME SELECTOR STATE ===
  const [chainFrameSelectorOpen, setChainFrameSelectorOpen] = useState<{
    segmentId: string;
    targetImage: 'start' | 'end';
  } | null>(null);

  // === SESSION PERSISTENCE ===
  const {
    profile,
    sessionId,
    isSaving,
    login,
    startNewSession,
    restoreSession,
    listHistory
  } = useSessionPersistence(
    promptSequence,
    sequenceBoundDogma || activeDogma,
    sequenceVideoData
  );

  // === TIMELINE STATE (from useTimelineState hook) ===
  const {
    timelineState,
    setTimelineState,
    selectedGap,
    setSelectedGap,
    iaPanelTab,
    setIaPanelTab,
    isVerticalToggle,
    setIsVerticalToggle,
    pushToHistory,
    handleUndo,
    handleRedo,
    totalDuration,
  } = timelineHook;

  // === CONFIG SETTINGS ===
  const handleSetApiKey = useCallback((key: string) => {
    setApiKey(key);
    setRuntimeApiKey(key);
    setHasCustomKey(true);
  }, [setApiKey, setHasCustomKey]);

  // === ONBOARDING RESET ===
  const handleResetOnboarding = useCallback(() => {
    localStorage.removeItem('studio_jenial_onboarding_seen_v2');
    setOnboardingKey(prev => prev + 1);
  }, [setOnboardingKey]);

  const {
    isPlaying,
    currentTime,
    play,
    pause,
    seek,
    togglePlayPause,
    engine
  } = usePlaybackEngine({
    fps: timelineState.project?.fps || 25,
    totalDuration,
    onTimeUpdate: (time) => {
      // Sync React state with engine time
      setTimelineState(prev => ({ ...prev, playheadSec: time }));
    }
  });

  // === GLOBAL KEYBOARD SHORTCUTS ===

  // Auto-open profile modal if no profile, but NOT if onboarding is active
  useEffect(() => {
    // If onboarding is not seen yet, prioritize it
    const hasSeenOnboarding = localStorage.getItem('studio_jenial_onboarding_seen_v2');
    if (!hasSeenOnboarding) return;

    const timer = setTimeout(() => {
      if (!profile && !localStorage.getItem('studio_profile_id')) {
        setIsProfileModalOpen(true);
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [profile]);

  // === GOOGLE DRIVE OAUTH HANDLER ===
  // Handle OAuth callback - split flow pattern
  useEffect(() => {
    // Parse query params from hash (HashRouter support: /#/studio?drive=pending)
    const getHashParams = () => {
      const search = window.location.search;
      if (search && search.length > 1) return new URLSearchParams(search);

      const hash = window.location.hash; // "#/studio?drive=pending&..."
      const queryIndex = hash.indexOf('?');
      if (queryIndex === -1) return new URLSearchParams();
      return new URLSearchParams(hash.slice(queryIndex + 1));
    };

    const params = getHashParams();
    const driveStatus = params.get('drive');

    // Helper to clean URL (handles both hash and regular URLs)
    const clearDriveParams = () => {
      if (window.location.hash.includes('?')) {
        // HashRouter: clean hash query params
        const baseHash = window.location.hash.split('?')[0];
        window.history.replaceState({}, document.title, window.location.pathname + baseHash);
      } else {
        // Regular URL: clean search params
        const newUrl = window.location.pathname;
        window.history.replaceState({}, document.title, newUrl);
      }
    };

    // PENDING: Callback redirected here with code - complete the exchange
    if (driveStatus === 'pending') {
      const code = params.get('code');
      const state = params.get('state');
      const requestId = params.get('requestId');

      if (code && state) {
        console.log(`[Studio][Drive] Completing OAuth exchange... (requestId: ${requestId})`);

        // Complete the exchange with JWT
        (async () => {
          try {
            const session = await supabase?.auth.getSession();
            const jwt = session?.data.session?.access_token;

            if (!jwt) {
              console.error('[Studio][Drive] No JWT available for exchange');
              alert('Erreur: Vous devez être connecté pour lier Google Drive.');
              clearDriveParams();
              return;
            }

            const response = await fetch('/api/google/drive/exchange', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${jwt}`
              },
              body: JSON.stringify({ code, state })
            });

            const result = await response.json();

            if (result.success) {
              console.log('[Studio][Drive] Exchange successful!');
              // Show success notification
              setShowStatusMessage('✅ Google Drive connecté avec succès !\n\nVos vidéos seront automatiquement sauvegardées dans votre Drive.');
              // Update URL to reflect success
              window.history.replaceState({}, document.title, `${window.location.pathname}?drive=connected`);
              // Trigger settings to show Drive connected
              setActiveSettingsTab('integrations');
              setShowApiKeyDialog(true);
              // Clean URL after a small delay
              setTimeout(clearDriveParams, 100);
            } else {
              console.error('[Studio][Drive] Exchange failed:', result.message);
              alert(`Échec de la connexion Drive: ${result.message || 'Erreur inconnue'}`);
              clearDriveParams();
            }
          } catch (error) {
            console.error('[Studio][Drive] Exchange error:', error);
            alert('Erreur lors de la connexion à Google Drive. Veuillez réessayer.');
            clearDriveParams();
          }
        })();
      } else {
        console.warn('[Studio][Drive] Missing code or state in pending callback');
        clearDriveParams();
      }
      return; // Don't process other statuses
    }

    // CONNECTED: Already handled (legacy path or after exchange)
    if (driveStatus === 'connected') {
      setActiveSettingsTab('integrations');
      setShowApiKeyDialog(true);
      clearDriveParams();
      console.log('[Studio] Drive connected successfully, opening settings...');
    }
    // ERROR: Show error message
    else if (driveStatus === 'error') {
      const reason = params.get('reason');
      const details = params.get('details');
      console.error('[Studio] Drive connection failed:', reason, details);
      clearDriveParams();
      let msg = `Échec de la connexion Google Drive : ${reason || 'Erreur inconnue'}`;
      if (details) msg += `\n\nDétails: ${decodeURIComponent(details)}`;
      alert(msg);
    }
  }, []);

  // Start new session if profile exists but no session
  useEffect(() => {
    if (profile && !sessionId) {
      startNewSession();
    }
  }, [profile, sessionId, startNewSession]);

  const handleProfileConfirm = async (identifier: string) => {
    setIsLoadingHistory(true);
    try {
      await login(identifier);
      setIsProfileModalOpen(false);
      // startNewSession will trigger via effect
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleOpenHistory = async () => {
    setIsHistoryModalOpen(true);
    setIsLoadingHistory(true);
    try {
      const sessions = await listHistory();
      setHistorySessions(sessions);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleRestoreSession = async (session: any) => {
    setIsLoadingHistory(true);
    try {
      const { session: restoredSession, storyboards } = await restoreSession(session.id);

      // Restore State
      // 1. Prompts
      const restoredPromptSequence: PromptSequence = {
        id: restoredSession.id,
        dogmaId: restoredSession.dogma_id || null,
        status: PromptSequenceStatus.CLEAN,
        createdAt: restoredSession.created_at,
        mainPrompt: restoredSession.main_prompt || '',
        extensionPrompts: Array.isArray(restoredSession.extension_prompts)
          ? restoredSession.extension_prompts
          : [],
        dirtyExtensions: Array.isArray(restoredSession.dirty_extensions)
          ? restoredSession.dirty_extensions
          : []
      };
      setPromptSequence(restoredPromptSequence); // Fixed double set

      // 2. Dogma
      if (restoredSession.dogma_snapshot) {
        setSequenceBoundDogma(restoredSession.dogma_snapshot);
      } else if (restoredSession.dogma_id) {
        // Try to find in library
        const d = dogmas.find((dg: Dogma) => dg.id === restoredSession.dogma_id);
        if (d) setSequenceBoundDogma(d);
      }

      // 3. Videos
      if (restoredSession.sequence_video_data) {
        setSequenceVideoData(restoredSession.sequence_video_data);
      }

      // 4. Storyboards
      if (storyboards) {
        setStoryboardByIndex(storyboards);
      }

      // 5. Active Index
      setActivePromptIndex(restoredSession.active_prompt_index ?? null);

      setIsHistoryModalOpen(false);
    } catch (e) {
      console.error('Restore failed', e);
      setErrorMessage('Failed to restore session');
    } finally {
      setIsLoadingHistory(false);
    }
  };


  useEffect(() => {
    // Check server config to determine if we need user API key
    const initCheck = async () => {
      try {
        const config = await fetchGeminiConfig();

        // If server has key configured, no dialog needed
        if (config.hasServerKey) {
          setShowApiKeyDialog(false);
          setHasCustomKey(true);
          return;
        }

        // BYOK DEV BYPASS: Check localStorage for cached key
        const cachedKey = getRuntimeApiKey();
        const cachedFalKey = getProviderKey(ProviderKeys.FAL_LTX);

        if (cachedKey) {
          console.log('[Studio] Dev Bypass: Loaded API Keys from storage');
          setApiKey(cachedKey);
          if (cachedFalKey) setLtxApiKey(cachedFalKey);
          setHasCustomKey(true);
          setShowApiKeyDialog(false);
          return;
        }

        // BYOK Strict: Prompt if no key found
        // Always prompt if we don't have a server key and no memory key is set.
        if (!apiKey) {
          setShowApiKeyDialog(true);
          setHasCustomKey(false);
        }
      } catch {
        // Fallback: Assume BYOK needed if check fails
        if (!apiKey) {
          setShowApiKeyDialog(true);
          setHasCustomKey(false);
        }
      }
    };
    initCheck();
  }, [apiKey]); // Re-run if apiKey state changes (e.g. user entered it)

  // === EXTENDED PERSISTENCE: Sync Prompt/Dogma to Project ===
  // Auto-update project object with current work state (debounced)
  // Unify persistence: Sync Timeline/Prompt/Dogma to Project
  useEffect(() => {
    if (!activeProject) return;

    const timer = setTimeout(() => {
      const updates: any = {};
      let hasUpdates = false;

      // Sync Segments
      if (JSON.stringify((activeProject as any).segments) !== JSON.stringify(timelineState.segments)) {
        updates.segments = timelineState.segments;
        hasUpdates = true;
      }

      // Sync Prompt Sequence
      if (JSON.stringify((activeProject as any).promptSequence) !== JSON.stringify(promptSequence)) {
        updates.promptSequence = promptSequence;
        hasUpdates = true;
      }

      // Sync Dogma
      if (activeDogma && JSON.stringify((activeProject as any).dogma) !== JSON.stringify(activeDogma)) {
        updates.dogma = activeDogma;
        hasUpdates = true;
      }

      if (hasUpdates) {
        updateProject(updates);
      }
    }, 2000); // 2s debounce

    return () => clearTimeout(timer);
  }, [promptSequence, activeDogma, timelineState.segments, activeProject, updateProject]);

  // Restore Extended State from Project
  useEffect(() => {
    if (timelineState.project) {
      // Restore Prompt Sequence if empty and present in project
      if ((timelineState.project as any).promptSequence && (!promptSequence)) {
        setPromptSequence((timelineState.project as any).promptSequence);
        console.log('[Studio] Restored PromptSequence from project');
      }
      // Restore Dogma if present
      if ((timelineState.project as any).dogma && !activeDogmaId) {
        setActiveDogmaId((timelineState.project as any).dogma.id);
        console.log('[Studio] Restored Dogma from project');
      }
    }
  }, [timelineState.project?.id]); // Run only when project ID changes (load)



  const confirmUnsavedVideo = useCallback(() => {
    if (appState === AppState.SUCCESS && !isCurrentVideoSaved) {
      return window.confirm(
        'You have an unsaved video. Are you sure you want to continue without saving? This action will discard the current video.',
      );
    }
    return true; // Continue if not in success state or if video is saved
  }, [appState, isCurrentVideoSaved]);

  const showStatusError = (message: string) => {
    setErrorMessage(message);
    setAppState(AppState.ERROR);
  };

  const handleCancelGeneration = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      console.log('Video generation cancelled by user.');
    }
  }, []);

  // === TIMELINE HANDLERS (Phase A Integration) ===

  // === EMERGENCY BACKUP: JSON SAVE/LOAD ===
  const handleSaveProjectJson = useCallback(() => {
    const backupData = {
      version: '1.0',
      timestamp: new Date().toISOString(),
      timelineState,
      promptSequence,
      sequenceVideoData // Also save existing video references
    };

    const blob = new Blob([JSON.stringify(backupData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `project_backup_${new Date().getTime()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [timelineState, promptSequence, sequenceVideoData]);

  const handleExport = useCallback(async (format: 'aaf' | 'fcp_xml' | 'fcpx_xml' | 'json' = 'json') => {
    try {
      if (format === 'json') {
        // Native JSON export (existing behavior roughly, but specific to OTIO now)
        const otioData = timelineStateToOTIO(timelineState);
        const blob = new Blob([JSON.stringify(otioData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `timeline_export_${Date.now()}.otio`;
        a.click();
        return;
      }

      // Bridge Conversion
      const otioData = timelineStateToOTIO(timelineState);
      const otioJson = JSON.stringify(otioData);

      const res = await fetch('http://localhost:8000/convert', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          otio_json: otioJson,
          format: format
        })
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Conversion failed');
      }

      // Download binary result
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Filename from header or fallback
      const contentDisposition = res.headers.get('content-disposition');
      let filename = `export.${format}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match) filename = match[1];
      }
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);

    } catch (error: any) {
      console.error('[Export] Failed:', error);
      alert(`Export failed: ${error.message}. Ensure OTIO Bridge is running.`);
    }
  }, [timelineState]);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleLoadProjectJson = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const json = JSON.parse(event.target?.result as string);
        if (json.timelineState) {
          setTimelineState(json.timelineState);
          console.log('[Backup] Timeline restored');
        }
        if (json.promptSequence) {
          setPromptSequence(json.promptSequence);
          console.log('[Backup] PromptSequence restored');
        }
        if (json.sequenceVideoData) {
          setSequenceVideoData(json.sequenceVideoData);
          console.log('[Backup] VideoData restored');
        }
        alert('Projet restauré avec succès !');
      } catch (err) {
        console.error('Failed to parse backup', err);
        alert('Erreur: Fichier de sauvegarde invalide.');
      }
    };
    reader.readAsText(file);
    // Reset input
    e.target.value = '';
  }, []);

  // === TIMELINE ACTIONS HOOK ===
  const {
    handleSegmentReorder,
    handleSegmentDelete,
    handleDeleteGap,
    handleSegmentDuplicate,
    handleSplitSegment,
    handleSegmentTrim,
    handleSegmentMove
  } = useTimelineActions({
    timelineState,
    setTimelineState,
    pushToHistory,
    setSelectedGap
  });

  const handleTogglePlayback = useCallback(() => {
    if (engine) {
      if (isPlaying) engine.pause();
      else engine.play();
    }
  }, [engine, isPlaying]);

  const videoRef = useRef<HTMLVideoElement>(null);

  const handleSeek = (time: number) => {
    seek(time);
    // State sync handled by engine onTimeUpdate
  };


  const handleUpdateSegment = (id: string, updates: Partial<SegmentWithUI>) => {
    setTimelineState(prev => ({
      ...prev,
      segments: prev.segments.map(s => s.id === id ? { ...s, ...updates } : s)
    }));
  };

  // === FPS & QUANTIZATION HELPERS ===
  const quantizeSec = useCallback((sec: number, fps: number) => Math.round(sec * fps) / fps, []);

  const mapSourceFpsToSupported = useCallback((sourceFps: number): FPS => {
    if (sourceFps >= 23.5 && sourceFps <= 24.5) return 24;
    if (sourceFps >= 24.5 && sourceFps <= 26.5) return 25;
    if (sourceFps >= 29.5 && sourceFps <= 31.5) return 30;
    if (sourceFps >= 48 && sourceFps <= 52) return 50;
    if (sourceFps >= 58 && sourceFps <= 62) return 60;
    console.log(`[FPS] Unrecognized source FPS ${sourceFps}, falling back to 25`);
    return 25;
  }, []);

  const handleFileImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    try {
      setAppState(AppState.LOADING);
      setShowStatusMessage(`Importation de ${files.length} fichier(s)...`);

      let firstValidFps: number | null = null;

      for (const file of Array.from(files)) {
        const result = await uploadMedia(file, 'rushes');
        if (result.success && result.media?.metadata?.fps && !firstValidFps) {
          firstValidFps = result.media.metadata.fps;
        }
      }

      // Auto-adjust project FPS if timeline is empty
      if (firstValidFps && timelineState.segments.length === 0) {
        const mappedFps = mapSourceFpsToSupported(firstValidFps);
        console.log(`[FPS] First import detected ${firstValidFps}fps. Setting project FPS to ${mappedFps}.`);
        setTimelineState(prev => ({
          ...prev,
          project: prev.project ? { ...prev.project, fps: mappedFps } : {
            id: activeProject?.id || `proj-${Date.now()}`,
            userId: 'user',
            title: activeProject?.name || 'Projet Vidéo',
            fps: mappedFps,
            aspect: '16:9',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          } as any
        }));
      }

      setShowStatusMessage('Importation terminée');
    } catch (err) {
      console.error('[Import] Failed to import files:', err);
      setErrorMessage('Erreur lors de l\'importation des fichiers');
    } finally {
      setAppState(AppState.IDLE);
      setTimeout(() => setShowStatusMessage(null), 2000);
      if (e.target) e.target.value = '';
    }
  };

  const _handleSlotGenerate = async (binId: string, slotId: string) => {
    const bin = getBinById(binId);
    const slot = getSlotById(binId, slotId);

    if (!bin || !slot) {
      console.error('[Slot] Bin or Slot not found', { binId, slotId });
      return;
    }

    if (!apiKey && !hasCustomKey) {
      setShowApiKeyDialog(true);
      return;
    }

    try {
      setSlotStatus(binId, slotId, 'generating');
      console.log(`[Slot] Starting generation for slot ${slotId} in bin ${binId}`);

      const projectFps = timelineState.project?.fps || 25;

      const genParams: GenerateVideoParams = {
        prompt: slot.prompt,
        model: VeoModel.VEO,
        aspectRatio: AspectRatio.LANDSCAPE,
        resolution: Resolution.P720,
        mode: GenerationMode.TEXT_TO_VIDEO,
        duration: slot.duration,
        startFrame: slot.keyframe,
        dogma: bin.dogmaSnapshot || activeDogma || undefined,
        provider: videoSettings?.engine === 'ltx2_fal' ? VideoProvider.LTX : VideoProvider.VERTEX,
        fps: projectFps,
        // Inject startFrameMode from videoSettings (with null guard)
        startFrameMode: videoSettings?.startFrameMode || 'reference_soft',
      };

      const res = await generateVideo(
        genParams,
        new AbortController().signal,
        undefined,
        apiKey || undefined
      );

      const thumbnail = await generateThumbnail(res.blob);

      const slotVideo: SlotVideo = {
        id: crypto.randomUUID(),
        url: res.objectUrl,
        blob: res.blob,
        thumbnail,
        generatedAt: new Date().toISOString(),
        duration: slot.duration, // Planned duration
        veoUri: res.uri
      };

      fillSlotWithVideo(binId, slotId, slotVideo);
      console.log(`[Slot] Generation successful for slot ${slotId}`);
    } catch (err: any) {
      console.error(`[Slot] Generation failed for slot ${slotId}:`, err);
      setSlotStatus(binId, slotId, 'error', err instanceof Error ? err.message : String(err));

      if (err?.isApiKeyError || err?.message?.includes('API_KEY_ERROR')) {
        setShowApiKeyDialog(true);
      }
    }
  };

  const _handleSlotAddToTimeline = (binId: string, slotId: string) => {
    const slot = getSlotById(binId, slotId);
    if (!slot || !slot.video) {
      console.warn('[Slot] Cannot add to timeline: slot not ready or missing video');
      return;
    }

    pushToHistory();

    const fps = timelineState.project?.fps || 25;
    const projectInSec = quantizeSec(timelineState.playheadSec, fps);
    const durationSec = slot.video.duration || slot.duration;
    const durationFrames = Math.round(durationSec * fps);
    // Recalculate durationSec from rounded frames to avoid precision drift
    const finalDurationSec = durationFrames / fps;
    const projectOutSec = projectInSec + finalDurationSec;

    // Create a segment for the timeline
    const newSegment: SegmentWithUI = {
      id: crypto.randomUUID(),
      projectId: activeProject?.id || 'default',
      trackId: 'v1',
      order: timelineState.segments.length,
      inSec: projectInSec,
      outSec: projectOutSec,
      durationSec: finalDurationSec,
      startFrame: Math.round(projectInSec * fps),
      durationFrames: durationFrames,
      label: `${slot.shotType} (Gen)`,
      locked: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      uiState: 'idle',
      mediaKind: 'generated',
      mediaType: 'video',
      mediaSrc: slot.video.url,
      activeRevision: {
        id: crypto.randomUUID(),
        segmentId: '',
        status: 'succeeded',
        createdAt: new Date().toISOString(),
        videoUrl: slot.video.url,
        thumbnailUrl: slot.video.thumbnail
      }
    };

    if (newSegment.activeRevision) {
      newSegment.activeRevision.segmentId = newSegment.id;
    }

    setTimelineState(prev => ({
      ...prev,
      segments: [...prev.segments, newSegment].sort((a, b) => a.inSec - b.inSec)
    }));

    console.log(`[Slot] Added slot ${slotId} to timeline at ${projectInSec}s (${fps}fps)`);
  };


  // === KEYBOARD SHORTCUTS (NLE) ===
  const DEFAULT_PLAYBACK_FPS = 30; // Fallback for shortcuts

  const handleSeekForward = useCallback(() => {
    const fps = timelineState.project?.fps || DEFAULT_PLAYBACK_FPS;
    seek(currentTime + (10 / fps));
  }, [currentTime, seek, timelineState.project?.fps]);

  const handleSeekBackward = useCallback(() => {
    const fps = timelineState.project?.fps || DEFAULT_PLAYBACK_FPS;
    seek(currentTime - (10 / fps));
  }, [currentTime, seek, timelineState.project?.fps]);

  useStudioHotkeys({
    activeTab,
    sourceMedia,
    timelineState,
    setTimelineState,
    handleUndo,
    handleRedo,
    togglePlayPause,
    handleSplitSegment,
    handleDeleteGap,
    handleSegmentDelete,
    selectedGap,
    FPS: DEFAULT_PLAYBACK_FPS,
    currentTime,
    seek,
    handleSeekForward,
    handleSeekBackward
  });



  // === HELPER: Generate Keyframe (Nano) ===
  const generateKeyframe = useCallback(async (segmentIndex: number, prompt: string) => {
    try {
      // Use bound dogma if available, otherwise active
      const dogmaToUse = sequenceBoundDogma ?? activeDogma;
      console.log(`[ImageFirst] Generating keyframe for segment ${segmentIndex} with dogma:`, dogmaToUse?.title);

      // P0: Use quality:'pro' for root keyframes (segment 0)
      const quality = segmentIndex === 0 ? 'pro' : undefined;
      console.log(`[ImageFirst] Using quality=${quality || 'default'} for segment ${segmentIndex}`);

      const result = await generateNanoPreview({
        textPrompt: prompt,
        dogma: dogmaToUse,
        quality: quality || 'pro',
        apiKey: apiKey || undefined, // P0.7: BYOK
        characters,
      });

      if (result.previewImage) {
        setStoryboardByIndex(prev => ({
          ...prev,
          [segmentIndex]: {
            id: crypto.randomUUID(),
            owner: segmentIndex === 0 ? 'root' : 'extension',
            previewImage: result.previewImage,
            previewPrompt: result.previewPrompt || prompt,
            segmentIndex,
            cameraNotes: result.cameraNotes,
            movementNotes: result.movementNotes,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          }
        }));
        console.log(`[ImageFirst] Keyframe ${segmentIndex} generated successfully`);

        // If Root, we can now show the Mode Selection UI
        if (segmentIndex === 0) {
          setSequenceMode('plan-sequence');
        }
      }
    } catch (err: any) {
      console.warn(`[ImageFirst] Failed to generate keyframe ${segmentIndex}:`, err);

      // P0: Detect API key errors and show dialog
      if (err?.isApiKeyError || err?.message?.includes('API_KEY_ERROR')) {
        console.log('[ImageFirst] API key error detected, showing dialog');
        setShowApiKeyDialog(true);
        setErrorMessage('API key required or invalid. Please enter a valid Gemini API key.');
      } else {
        setErrorMessage(err instanceof Error ? err.message : 'Failed to generate preview.');
      }
    }
  }, [sequenceBoundDogma, activeDogma]);

  const handleGenerate = useCallback(
    async (params: GenerateVideoParams) => {
      const traceId = `seq-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;
      console.log('[SEQUENCE][GEN_START]', {
        traceId,
        mode: params.mode,
        model: params.model,
        promptLength: params.prompt?.length,
        hasStartFrame: !!params.startFrame,
        activePromptIndex,
        sequenceLength: promptSequence ? promptSequence.extensionPrompts.length + 1 : 0,
      });

      // BYOK Strict: Check React State
      if (!apiKey && !hasCustomKey) {
        console.log('[SEQUENCE][BLOCKED]', { traceId, reason: 'no_api_key' });
        setShowApiKeyDialog(true);
        return;
      }

      abortControllerRef.current = new AbortController();

      let currentPromptIndex = -1;
      if (promptSequence) {
        if (activePromptIndex !== null) {
          currentPromptIndex = activePromptIndex;
        } else {
          const allPrompts = [
            promptSequence.mainPrompt,
            ...promptSequence.extensionPrompts,
          ];
          const matchingIndex = allPrompts.findIndex(
            (p) => p === params.prompt,
          );
          if (matchingIndex !== -1) {
            currentPromptIndex = matchingIndex;
          }
        }

        if (currentPromptIndex !== -1) {
          setActivePromptIndex(currentPromptIndex);
          setSequenceProgress({
            current: currentPromptIndex + 1,
            total: promptSequence.extensionPrompts.length + 1,
          });
        } else {
          setActivePromptIndex(null);
          setSequenceProgress(null);
        }
      } else {
        setActivePromptIndex(null);
        setSequenceProgress(null);
      }

      // ═══════════════════════════════════════════════════════════════════
      // GUARDRAIL 1 & 2: Validate base video for extensions, enforce mode
      // ═══════════════════════════════════════════════════════════════════
      let effectiveParams = {
        ...params,
        // Inject startFrameMode from videoSettings (for first frame control, with null guard)
        startFrameMode: videoSettings?.startFrameMode || 'reference_soft',
      };

      // ═══════════════════════════════════════════════════════════════════
      // P0.4 & P0.5: Inject start frame from keyframe (priority order)
      // 1. User explicitly selected a keyframe via "Use as Start Image"
      // 2. Current segment's keyframe (if exists)
      // 3. assistantImage (fallback)
      // ═══════════════════════════════════════════════════════════════════
      const resolveStartFrame = () => {
        // Priority 1: Explicitly selected keyframe
        if (activeStartImageIndex !== null && storyboardByIndex[activeStartImageIndex]?.previewImage) {
          console.log('[P0.4] Using explicitly selected keyframe as startFrame:', activeStartImageIndex);
          return storyboardByIndex[activeStartImageIndex].previewImage;
        }
        // Priority 2: Current segment's keyframe (auto-fill)
        const segmentKeyframe = storyboardByIndex[currentPromptIndex];
        if (segmentKeyframe?.previewImage) {
          console.log('[P0.4] Auto-filling startFrame from segment keyframe:', currentPromptIndex);
          return segmentKeyframe.previewImage;
        }
        // Priority 3: assistantImage (existing fallback)
        if (assistantImage) {
          console.log('[P0.4] Using assistantImage as startFrame fallback');
          return assistantImage;
        }
        return null;
      };

      const resolvedStartFrame = resolveStartFrame();
      if (resolvedStartFrame && !effectiveParams.startFrame) {
        effectiveParams = {
          ...effectiveParams,
          startFrame: resolvedStartFrame,
        };
        console.log('[P0.5] StartFrame injected:', {
          hasBase64: !!resolvedStartFrame.base64,
          hasFile: !!resolvedStartFrame.file,
        });
      }

      if (promptSequence && currentPromptIndex !== -1) {
        if (currentPromptIndex === 0) {
          // ROOT SHOT: Must be TEXT_TO_VIDEO, no base video
          console.log('[Sequence] Root shot index=0 → mode=TEXT_TO_VIDEO (no base video)');

          // IMAGE-FIRST ENFORCEMENT
          // If no keyframe exists for the root shot, we MUST generate one first via Nano
          if (!storyboardByIndex[0]) {
            console.log('[ImageFirst] No keyframe found for root shot. Enforcing Nano preview.');
            // Generate keyframe using the prompt
            generateKeyframe(0, effectiveParams.prompt);
            // Optionally set state to indicate we are in preview mode
            // Inform user via temporary message (using error message slot for visibility, or console)
            console.log('[ImageFirst] Preview generation triggered. Pausing video generation.');
            // Abort video generation
            return;
          }

          // Force TEXT_TO_VIDEO for root shot (unless using frames/references mode)
          if (effectiveParams.mode === GenerationMode.EXTEND_VIDEO) {
            console.warn('[Sequence] WARNING: Root shot had EXTEND_VIDEO mode, forcing TEXT_TO_VIDEO');
            effectiveParams = {
              ...effectiveParams,
              mode: GenerationMode.TEXT_TO_VIDEO,
              inputVideoObject: null,
            };
          }
        } else {
          // EXTENSION: Must have base video with valid URI
          const baseVideoData = sequenceVideoData[currentPromptIndex - 1];
          const baseVideo = baseVideoData?.video;
          const baseVideoUri = baseVideo?.uri;

          if (!baseVideoUri) {
            // GUARDRAIL 1: Block extension without base video
            console.warn(`[Sequence] Cannot generate extension index=${currentPromptIndex}: missing base video for index=${currentPromptIndex - 1}`);
            console.warn('[Sequence] WARNING: extension requested without base video, blocking generation');
            setErrorMessage(
              `The previous shot (Shot ${currentPromptIndex}) is not ready yet. Please generate or wait for the previous video before creating this extension.`
            );
            return; // BLOCK - do not proceed
          }

          // GUARDRAIL 2: Log and enforce extend mode with proper base video
          console.log(`[Sequence] Preparing extension index=${currentPromptIndex} with base video from index=${currentPromptIndex - 1}: { uri: "${baseVideoUri}" }`);
          console.log(`[Sequence] Extension index=${currentPromptIndex} → mode=EXTEND_VIDEO, baseVideoUri=${baseVideoUri}`);

          // Force EXTEND_VIDEO mode with the correct base video
          effectiveParams = {
            ...effectiveParams,
            mode: GenerationMode.EXTEND_VIDEO,
            inputVideoObject: baseVideo,
          };
        }
      }
      // ═══════════════════════════════════════════════════════════════════
      // EXTERNAL VIDEO CONTINUATION: Use TEXT_TO_VIDEO with last frame
      // ═══════════════════════════════════════════════════════════════════
      // Veo Extension mode ONLY works with Veo-generated videos.
      // For external videos, we use TEXT_TO_VIDEO with the last frame as startFrame.
      // The AI prompt already includes continuity context, so the result is seamless.
      // EXTERNAL VIDEO DETECTION: Only trigger for explicitly external sources
      // isExternalVideoSource is ONLY set by handleStartExternalExtensionAssistant
      if (
        effectiveParams.mode === GenerationMode.EXTEND_VIDEO &&
        isExternalVideoSource &&
        originalVideoForExtension?.file
      ) {
        console.log('[External Video] Detected external video continuation request', {
          rootVideoName: originalVideoForExtension.file.name,
          hasExtensionContext: !!assistantExtensionContext,
        });

        // Switch to TEXT_TO_VIDEO mode with the last frame as visual anchor
        console.log('[External Video] Using TEXT_TO_VIDEO mode with startFrame (Veo Extension only supports Veo-generated videos)');

        effectiveParams = {
          ...effectiveParams,
          mode: GenerationMode.TEXT_TO_VIDEO,
          inputVideoObject: null, // Clear video reference - we use startFrame instead
          startFrame: assistantExtensionContext || effectiveParams.startFrame,
        };

        console.log('[External Video] Converted to TEXT_TO_VIDEO with startFrame:', {
          hasStartFrame: !!effectiveParams.startFrame,
          prompt: effectiveParams.prompt?.substring(0, 100) + '...',
        });
      }

      setAppState(AppState.LOADING);
      setErrorMessage(null);
      setLastConfig(effectiveParams);
      setInitialFormValues(null);

      if (
        !assistantExtensionContext &&
        effectiveParams.mode !== GenerationMode.EXTEND_VIDEO
      ) {
        setAssistantExtensionContext(null);
        setAssistantMotionDescription(null);
        setOriginalVideoForExtension(null);
        setIsExternalVideoSource(false); // Reset flag
      }

      // Don't clear assistantImage immediately so context radar persists during load
      // setAssistantImage(null);
      setAssistantReferenceVideo(null);
      setMentionedCharacters([]);

      if (currentPromptIndex === 0) {
        setMainPromptConfig(effectiveParams);
      }

      try {
        // GUARDRAIL 4: Log the call for chain verification
        if (promptSequence && currentPromptIndex > 0) {
          const baseVideoUri = effectiveParams.inputVideoObject?.uri;
          console.log(`[Sequence] Calling generateVideo for extension index=${currentPromptIndex} with mode=EXTEND_VIDEO and baseVideoUri=${baseVideoUri}`);
        }

        // Use Gemini API or LTX depending on provider
        let res;

        if (effectiveParams.provider === VideoProvider.LTX) {
          console.log('[LTX] Provider selected');

          const lp = effectiveParams.ltxParams; // Shorthand

          // Build complete LTX payload from ltxParams
          const ltxPayload: Record<string, any> = {
            prompt: effectiveParams.prompt,
            // Core generation params
            num_inference_steps: lp?.num_inference_steps || 30,
            guidance_scale: lp?.guidance_scale || 3.0,
            negative_prompt: lp?.negative_prompt || '',
            seed: lp?.seed, // undefined = random
            // Duration/Frames - Fal.ai uses num_frames, calculate from duration * fps
            num_frames: Math.ceil((lp?.duration_sec || 8) * (lp?.fps || 25)),
            // Enhancement
            expand_prompt: lp?.enhance_prompt ?? true,
          };

          // Camera motion (if supported)
          if (lp?.camera_motion && lp.camera_motion !== 'static') {
            ltxPayload.camera_motion = lp.camera_motion;
          }

          // Audio generation
          if (lp?.generate_audio) {
            ltxPayload.enable_audio = true;
            if (lp.audio_prompt) {
              ltxPayload.audio_prompt = lp.audio_prompt;
            }
          }

          // Control models (IC-LoRA)
          if (lp?.control_type && lp.control_type !== 'none') {
            ltxPayload.control_type = lp.control_type;
            if (lp.control_image) {
              ltxPayload.control_image = lp.control_image;
            }
            if (lp.control_strength !== undefined) {
              ltxPayload.control_strength = lp.control_strength;
            }
          }

          // Handle Input Image for i2v mode
          if (effectiveParams.mode === GenerationMode.REFERENCES_TO_VIDEO || effectiveParams.mode === GenerationMode.FRAMES_TO_VIDEO) {
            const refImg = effectiveParams.startFrame || effectiveParams.referenceImages?.[0];
            if (refImg?.base64) {
              ltxPayload.image_url = `data:${refImg.file.type};base64,${refImg.base64}`;
            }
          }

          // Determine Endpoint based on mode
          let endpointId = 'fal-ai/ltx-video/text-to-video';
          if (effectiveParams.mode !== GenerationMode.TEXT_TO_VIDEO && ltxPayload.image_url) {
            endpointId = 'fal-ai/ltx-video/image-to-video';
          }

          // Structured logging (no secrets)
          console.log('[LTX] Submit', {
            provider: 'LTX-2',
            keyPresent: !!getProviderKey(ProviderKeys.FAL_LTX),
            endpointId,
            params: {
              promptPreview: ltxPayload.prompt?.slice(0, 60) + '...',
              steps: ltxPayload.num_inference_steps,
              guidance: ltxPayload.guidance_scale,
              frames: ltxPayload.num_frames,
              seed: ltxPayload.seed,
              camera: ltxPayload.camera_motion,
              audio: ltxPayload.enable_audio,
            }
          });

          const submitRes = await LTXService.submit({
            endpointId,
            input: ltxPayload
          });

          // 2. Poll
          console.log('[Studio] Generating with LTX-2...');
          const result = await LTXService.pollForResult(endpointId, submitRes.request_id);

          // 3. Format Result
          if (!result.video?.url) throw new Error('LTX did not return a video URL');

          // Fetch Blob for App consistency (thumbnails, uploads)
          const vidRes = await fetch(result.video.url);
          const vidBlob = await vidRes.blob();
          const objectUrl = URL.createObjectURL(vidBlob);

          res = {
            objectUrl,
            blob: vidBlob,
            video: { uri: result.video.url }, // Use remote URL as URI
            supabaseUrl: undefined // Or upload to supabase if needed?
          };

        } else {
          // Standard Veo / Gemini
          res = await generateVideo(
            effectiveParams,
            abortControllerRef.current.signal,
            undefined,
            apiKey || undefined // P0.7: BYOK
          );
        }
        const { objectUrl, blob, video } = res;

        if (promptSequence && currentPromptIndex !== -1) {
          try {
            const thumbnail = await generateThumbnail(blob);
            // GUARDRAIL 4: Clear chain logging
            if (currentPromptIndex === 0) {
              console.log(`[Sequence] Stored root video for index=0: { uri: "${video?.uri}" }`);
            } else {
              console.log(`[Sequence] Stored extension video for index=${currentPromptIndex}: { uri: "${video?.uri}" }`);
            }
            setSequenceVideoData((prev) => ({
              ...prev,
              [currentPromptIndex]: {
                video,
                blob,
                url: objectUrl,
                thumbnail,
                prompt: currentPromptIndex === 0 ? promptSequence.mainPrompt : (promptSequence.extensionPrompts[currentPromptIndex - 1] || ''),
                isExtension: currentPromptIndex > 0,
                status: 'completed' as const,
                createdAt: new Date().toISOString()
              },
            }));
          } catch (thumbError) {
            console.error(
              'Failed to generate thumbnail for sequence item.',
              thumbError,
            );
            setSequenceVideoData((prev) => ({
              ...prev,
              [currentPromptIndex]: {
                video,
                blob,
                url: objectUrl,
                thumbnail: '',
                prompt: currentPromptIndex === 0 ? promptSequence.mainPrompt : (promptSequence.extensionPrompts[currentPromptIndex - 1] || ''),
                isExtension: currentPromptIndex > 0,
                status: 'completed' as const,
                createdAt: new Date().toISOString()
              },
            }));
          }
        }

        // --- AUTO-SAVE: Systematically save generated shot to Library ---
        try {
          // Generate thumbnail (re-use if possible, but ensure we have one)
          const thumbnailToUse = await generateThumbnail(blob);

          // Check if Drive is connected for auto-upload
          let videoStorageInfo: SavedShot['videoStorage'] = undefined;

          // Try Supabase first (if available from provider)
          if (res.supabaseUrl) {
            videoStorageInfo = {
              type: 'supabase',
              reference: res.supabaseUrl.split('/').pop() || '',
              url: res.supabaseUrl
            };
          }

          // If no Supabase, try Google Drive with blob upload
          if (!videoStorageInfo) {
            try {
              const driveConnected = await isDriveConnected();
              if (driveConnected && blob) {
                console.log('[Studio] Drive connected, uploading video blob...');
                // Get user's selected Drive folder for uploads
                const userDriveFolderId = localStorage.getItem('jenial_drive_folder_id') || undefined;
                // Use uploadBlobToDrive instead of uploadToDrive (blob URLs can't be fetched by server)
                const driveResult = await uploadBlobToDrive(blob, `shot_${Date.now()}.mp4`, 'video/mp4', userDriveFolderId);
                if (driveResult?.success && driveResult.fileId) {
                  videoStorageInfo = {
                    type: 'google-drive',
                    reference: driveResult.fileId,
                    url: driveResult.webViewLink || `https://drive.google.com/file/d/${driveResult.fileId}/view`
                  };
                  console.log('[Studio] Video uploaded to Drive:', driveResult.fileId);
                } else if (driveResult?.error) {
                  console.warn('[Studio] Drive upload returned error:', driveResult.error);
                }
              }
            } catch (driveErr) {
              console.warn('[Studio] Drive upload failed:', driveErr);
              // Continue without Drive storage
            }
          }

          const newShot: SavedShot = {
            id: crypto.randomUUID(),
            title: effectiveParams.prompt?.substring(0, 30) || 'Auto-Saved Shot',
            prompt: effectiveParams.prompt,
            thumbnail: thumbnailToUse,
            createdAt: new Date().toISOString(),
            model: effectiveParams.model,
            aspectRatio: effectiveParams.aspectRatio,
            resolution: effectiveParams.resolution,
            mode: effectiveParams.mode,
            videoUrl: objectUrl,
            videoStorage: videoStorageInfo
          };

          handleSaveShotToLibrary(newShot);
          console.log('[Studio] Auto-saved shot to library:', newShot.id, videoStorageInfo ? `(${videoStorageInfo.type})` : '(local)');
        } catch (e) {
          console.warn('[Studio] Auto-save failed:', e);
        }
        // ----------------------------------------------------------------

        console.log('[SEQUENCE][GEN_SUCCESS]', {
          traceId,
          segmentIndex: currentPromptIndex,
          blobSize: blob?.size,
          hasAutoSaved: true,
        });

        setVideoUrl(objectUrl);
        setLastVideoBlob(blob);
        setLastVideoObject(video);
        setAppState(AppState.SUCCESS);
        setCurrentStage(AppStage.RESULT);
        setIsCurrentVideoSaved(true); // Marked as saved since we auto-saved it!
        // Now clear assistant image as we move to result
        setAssistantImage(null);

        if (promptSequence && currentPromptIndex !== -1) {
          setActivePromptIndex(currentPromptIndex + 1);
        }
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          console.log('[SEQUENCE][GEN_ABORTED]', { traceId });
          setAppState(AppState.IDLE);
          setInitialFormValues(params);
          setErrorMessage(null);
          setActivePromptIndex(null);
          setSequenceProgress(null);
          return;
        }

        console.error('[SEQUENCE][GEN_FAILED]', { traceId, error: (error as Error).message });

        // Handle structured API errors from callVeoBackend
        const apiError = error as ApiError;
        if (apiError.status && apiError.error) {
          // Structured error from our backend
          if (apiError.status === 401 && apiError.error === 'API_KEY_MISSING') {
            setApiKeyError('Aucune clé API configurée. Veuillez entrer votre clé Gemini.');
            setShowApiKeyDialog(true);
            setAppState(AppState.IDLE);
            return;
          } else if (apiError.status === 401 && apiError.error === 'API_KEY_INVALID') {
            setApiKeyError('Clé API invalide. Vérifiez votre clé et réessayez.');
            setShowApiKeyDialog(true);
            setAppState(AppState.IDLE);
            return;
          } else if (apiError.status === 404 && apiError.error === 'MODEL_NOT_FOUND') {
            // Model errors: show in UI, DON'T open API key dialog
            showStatusError(`Le modèle Veo n'est pas disponible: ${apiError.data?.details || 'Vérifiez que votre clé API dispose de l\'accès aux modèles Veo 3.1.'}`);
            return;
          } else if (apiError.status === 400) {
            showStatusError(`Requête invalide: ${apiError.data?.details || 'Vérifiez vos paramètres.'}`);
            return;
          } else {
            showStatusError('Erreur serveur. Réessayez plus tard.');
            return;
          }
        }

        // Legacy error handling for Error instances
        const errorMessage =
          error instanceof Error ? error.message : 'An unknown error occurred.';

        let userFriendlyMessage = `Video generation failed: ${errorMessage}`;
        let shouldOpenDialog = false;
        let dialogErrorMsg: string | null = null;

        if (typeof errorMessage === 'string') {
          // Model errors: show in UI, DON'T open API key dialog
          if (errorMessage.includes('MODEL_NOT_FOUND') ||
            (errorMessage.toLowerCase().includes('model') && errorMessage.toLowerCase().includes('not found'))) {
            userFriendlyMessage = 'Le modèle Veo sélectionné n\'est pas disponible. Vérifiez que votre clé API dispose de l\'accès aux modèles Veo 3.1.';
            // Don't set shouldOpenDialog - model errors should NOT trigger key dialog
          } else if (
            errorMessage.includes('API_KEY_INVALID') ||
            errorMessage.includes('API key not valid') ||
            errorMessage.toLowerCase().includes('permission denied') ||
            errorMessage.includes('API_KEY_MISSING') ||
            errorMessage.includes('403')
          ) {
            userFriendlyMessage = 'Votre clé API est invalide, manquante ou ne dispose pas des permissions requises.';
            dialogErrorMsg = 'Clé API invalide ou manquante.';
            shouldOpenDialog = true;
          }
        }

        showStatusError(userFriendlyMessage);
        if (shouldOpenDialog) {
          setApiKeyError(dialogErrorMsg);
          setShowApiKeyDialog(true);
        }
      }
    },
    [promptSequence, activePromptIndex, assistantExtensionContext],
  );

  const handleStartNewProject = useCallback(() => {
    if (!confirmUnsavedVideo()) return;

    console.log('[StateTransition] handleStartNewProject - Full reset');

    // Core state
    setAppState(AppState.IDLE);
    setCurrentStage(AppStage.PROMPTING);
    setVideoUrl(null);
    setLastConfig(null);
    setLastVideoObject(null);
    setLastVideoBlob(null);

    // Sequence state - CRITICAL: Clear everything including bound dogma
    setPromptSequence(null);
    setSequenceBoundDogma(null);  // Clear sequence-bound dogma
    setActivePromptIndex(null);
    setSequenceProgress(null);

    // === CLEVER FIX: Persisted State Cleanup ===
    // Ensure we don't bleed state from previous sessions
    setSequenceVideoData({});
    setStoryboardByIndex({});
    setAssistantMessages([]);
    setShotCandidates([]); // Correct: Array
    setCandidatesByShotId({});
    setMainPromptConfig(null);

    // Reset UI state
    setEditingPromptDetails(null);
    setIsRevisingSequence(null);
    setMainPromptConfig(null);

    // Context state
    setInitialFormValues(null);
    setOriginalVideoForExtension(null);
    setAssistantExtensionContext(null);
    setAssistantImage(null);
    setAssistantReferenceVideo(null);
    setAssistantMotionDescription(null);
    setMentionedCharacters([]);
    setIsExternalVideoSource(false);

    // NOTE: activeDogma (global library selection) is preserved - user may want same dogma for new project
    console.log('[StateTransition] Reset complete. Global activeDogma preserved:', activeDogmaId);
    setResetKey(prev => prev + 1);
  }, [confirmUnsavedVideo, activeDogmaId]);

  // =========================================================================
  // NANO BANANA PRO: Centralized editor controller
  // =========================================================================

  /**
   * Derive target from segment index - strict mapping (no truthy/falsy)
   */
  const deriveTargetFromIndex = (segmentIndex: number | null): 'root' | 'extension' | 'character' => {
    if (segmentIndex === null) return 'character';
    if (segmentIndex === 0) return 'root';
    return 'extension';
  };

  /**
   * Open Nano editor from any entry point (stylet, thumbnails, drift, characters, infinite-chain)
   */
  const openNanoEditor = useCallback((opts: {
    segmentIndex: number | null;
    baseImage?: ImageFile;
    initialPrompt?: string;
    target?: 'root' | 'extension' | 'character';
    mode?: 'default' | 'decoupage-master-shot';
    onApply?: (result: NanoApplyPayload) => void;
  }) => {
    const target = opts.target || deriveTargetFromIndex(opts.segmentIndex);
    // CRITICAL: effectiveDogma = sequenceBoundDogma ?? activeDogma
    const effectiveDogma = promptSequence ? (sequenceBoundDogma ?? activeDogma) : activeDogma;

    console.log('[NanoEditor] Opening with context:', {
      segmentIndex: opts.segmentIndex,
      target,
      dogmaId: effectiveDogma?.id,
      hasBaseImage: !!opts.baseImage,
      hasCustomOnApply: !!opts.onApply,
    });

    setNanoEditorContext({
      segmentIndex: opts.segmentIndex,
      target,
      dogma: effectiveDogma,
      baseImage: opts.baseImage,
      initialPrompt: opts.initialPrompt,
      mode: opts.mode,
      onApply: opts.onApply,
    });
  }, [promptSequence, sequenceBoundDogma, activeDogma]);

  /**
   * Close Nano editor and reset context
   */
  const closeNanoEditor = useCallback(() => {
    console.log('[NanoEditor] Closing');
    setNanoEditorContext(null);
  }, []);

  /**
   * P0.4: Use a keyframe as Start Image for video generation
   * This sets the keyframe as the active reference and also sets assistantImage for fallback
   */
  const handleUseKeyframeAsStartImage = useCallback((segmentIndex: number, image: ImageFile) => {
    console.log('[P0.4] Setting keyframe as Start Image:', { segmentIndex });

    // Set the active index to track which keyframe is being used
    setActiveStartImageIndex(segmentIndex);

    // Also set assistantImage so it's used in generation
    setAssistantImage(image);

    // Clear any end image selection from this source
    if (activeEndImageIndex === segmentIndex) {
      setActiveEndImageIndex(null);
    }

    // Auto-fill mode: in extension mode, the previous segment's last frame should be start
    // But if user explicitly selects, we respect that
    console.log('[P0.4] Start Image set from segment', segmentIndex);
  }, [activeEndImageIndex]);

  /**
   * P0.4: Use a keyframe as End Image for video generation
   */
  const handleUseKeyframeAsEndImage = useCallback((segmentIndex: number, image: ImageFile) => {
    console.log('[P0.4] Setting keyframe as End Image:', { segmentIndex });

    setActiveEndImageIndex(segmentIndex);

    // Clear any start image selection from this source
    if (activeStartImageIndex === segmentIndex) {
      setActiveStartImageIndex(null);
    }
  }, [activeStartImageIndex]);

  /**
   * Handle Apply from Nano editor - updates prompts and storyboard
   * CRITICAL: Proper handling for root/extension/character
   */
  const handleNanoApply = useCallback((payload: NanoApplyPayload) => {
    console.log('[NanoEditor] Apply payload:', {
      target: payload.target,
      segmentIndex: payload.segmentIndex,
      promptLength: payload.previewPrompt.length,
    });

    // Helper: Trigger background upgrade for vignettes
    const triggerProUpgrade = async (idx: number) => {
      if (sequenceMode === 'decoupage' || payload.target === 'root') {
        console.log(`[NanoUpgrade] Upgrading segment ${idx} to Pro...`);
        try {
          const res = await generateNanoPreview({
            textPrompt: payload.previewPrompt,
            dogma: sequenceBoundDogma ?? activeDogma,
            quality: 'pro',
            target: payload.target
          });
          if (res.previewImage) {
            console.log(`[NanoUpgrade] Upgrade complete for ${idx}`);
            setStoryboardByIndex(prev => ({
              ...prev,
              [idx]: {
                ...prev[idx],
                id: crypto.randomUUID(),
                previewImage: res.previewImage,
                previewPrompt: res.previewPrompt || payload.previewPrompt // update prompt if refined
              }
            }));
          }
        } catch (e) { console.error('[NanoUpgrade] Failed:', e); }
      }
    };

    // Update storyboard preview
    const storyboardEntry: StoryboardPreview = {
      id: crypto.randomUUID(),
      owner: payload.target,
      segmentIndex: payload.segmentIndex ?? undefined,
      previewImage: payload.previewImage,
      previewPrompt: payload.previewPrompt,
      cameraNotes: payload.cameraNotes,
      movementNotes: payload.movementNotes,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    if (payload.target === 'root') {
      // === ROOT: Update mainPrompt + mark all extensions dirty ===
      if (!promptSequence) return;

      const extensionsCount = promptSequence.extensionPrompts.length;
      const updatedSequence: PromptSequence = {
        ...promptSequence,
        mainPrompt: payload.previewPrompt,
        status: extensionsCount > 0 ? PromptSequenceStatus.ROOT_MODIFIED : PromptSequenceStatus.CLEAN,
        // CRITICAL: Extensions are indexed 1..N (not 0..N-1)
        dirtyExtensions: extensionsCount > 0
          ? Array.from({ length: extensionsCount }, (_, i) => i + 1)
          : [],
        rootModifiedAt: new Date().toISOString(),
      };

      setPromptSequence(updatedSequence);
      setStoryboardByIndex(prev => ({ ...prev, [0]: storyboardEntry }));
      triggerProUpgrade(0);

      if (extensionsCount > 0) {
        setErrorMessage(`⚠️ Prompt root modifié. ${extensionsCount} extension(s) à régénérer.`);
      }

    } else if (payload.target === 'extension') {
      // === EXTENSION: Update specific extension + remove from dirty ===
      if (!promptSequence || payload.segmentIndex === null) return;

      // CRITICAL: extensionPrompts uses 0-based index, segmentIndex is 1-based
      const extensionIndex = payload.segmentIndex - 1;
      if (extensionIndex < 0 || extensionIndex >= promptSequence.extensionPrompts.length) {
        console.error('[NanoEditor] Invalid extension index:', extensionIndex);
        return;
      }

      const newExtensionPrompts = [...promptSequence.extensionPrompts];
      newExtensionPrompts[extensionIndex] = payload.previewPrompt;

      // Remove this extension from dirtyExtensions
      const newDirtyExtensions = (promptSequence.dirtyExtensions || [])
        .filter(idx => idx !== payload.segmentIndex);

      const updatedSequence: PromptSequence = {
        ...promptSequence,
        extensionPrompts: newExtensionPrompts,
        dirtyExtensions: newDirtyExtensions,
        status: newDirtyExtensions.length > 0
          ? PromptSequenceStatus.EXTENSIONS_DIRTY
          : PromptSequenceStatus.CLEAN,
      };

      setPromptSequence(updatedSequence);
      setStoryboardByIndex(prev => ({ ...prev, [payload.segmentIndex!]: storyboardEntry }));
      triggerProUpgrade(payload.segmentIndex!);

    } else if (payload.target === 'character') {
      // === CHARACTER: Update character asset (no dirty logic) ===
      // TODO: Implement character storyboard storage when character system is extended
      console.log('[NanoEditor] Character apply - storing preview');
    }

    // === CUSTOM onApply CALLBACK (used by InfiniteChain and other custom flows) ===
    if (nanoEditorContext?.onApply) {
      console.log('[NanoEditor] Calling custom onApply callback');
      nanoEditorContext.onApply(payload);
    }

    // Close editor and clear editing state
    closeNanoEditor();
    setEditingPromptDetails(null);
  }, [promptSequence, closeNanoEditor, nanoEditorContext]);

  const handleRetryLastPrompt = useCallback(() => {
    if (lastConfig) {
      if (!confirmUnsavedVideo()) return;
      setInitialFormValues(lastConfig);
      setCurrentStage(AppStage.PROMPTING);
    }
  }, [lastConfig, confirmUnsavedVideo]);

  const handleExtendVideo = useCallback(() => {
    if (lastVideoBlob && lastVideoObject) {
      if (!confirmUnsavedVideo()) return;

      const originalVideoFile = new File([lastVideoBlob], 'original.mp4', {
        type: lastVideoBlob.type,
      });

      setOriginalVideoForExtension({
        file: originalVideoFile,
        base64: '', // Not needed for object passing
      });

      const newConfig: GenerateVideoParams = {
        ...(lastConfig as GenerateVideoParams),
        prompt: '',
        mode: GenerationMode.EXTEND_VIDEO,
        inputVideoObject: lastVideoObject,
        startFrame: null,
        endFrame: null,
        referenceImages: [],
      };
      setInitialFormValues(newConfig);
      setCurrentStage(AppStage.PROMPTING);
    }
  }, [lastConfig, lastVideoBlob, lastVideoObject, confirmUnsavedVideo]);

  const handleContinueFromFrame = useCallback(
    (frame: ImageFile) => {
      if (!confirmUnsavedVideo()) return;
      const newConfig: GenerateVideoParams = {
        ...(lastConfig as GenerateVideoParams),
        prompt: '',
        mode: GenerationMode.FRAMES_TO_VIDEO,
        startFrame: frame,
        endFrame: null,
        referenceImages: [],
        inputVideo: null,
        inputVideoObject: null,
      };
      setInitialFormValues(newConfig);
      setAssistantImage(frame); // Sync start frame to assistant image automatically
      setCurrentStage(AppStage.PROMPTING);
    },
    [lastConfig, confirmUnsavedVideo],
  );

  const handleEditCapturedFrame = (frame: ImageFile) => {
    setFrameToEdit(frame);
    setCurrentStage(AppStage.EDITING);
  };

  const handleFrameEditConfirm = (newImage: ImageFile) => {
    handleContinueFromFrame(newImage);
  };

  const handleApplyNano = (newImage: ImageFile) => {
    console.log('[Nano] Applying retouched image to current context');
    setAssistantImage(newImage);
  };

  // Handlers for API Key Dialog (BYOK Strict Mode)
  // Uses existing apiKey state (line 383) + Memory Vault
  const handleApiKeySet = (newKey: string) => {
    const trimmed = newKey.trim();
    if (trimmed.length >= 20) {
      setApiKey(trimmed);
      setRuntimeApiKey(trimmed); // Persist to Memory Vault
      setShowApiKeyDialog(false);
      setApiKeyError(null);
      setHasCustomKey(true);
    } else {
      setApiKeyError('Invalid API Key (too short)');
    }
  };

  const handleApiKeyClear = () => {
    setApiKey(null);
    setRuntimeApiKey(null); // Clear Memory Vault
    setShowApiKeyDialog(false);
    setHasCustomKey(false);
  };

  // Import storeMedia (ensure this is imported at top of file, or use dynamic import if needed, but better top level.
  // Since I can't easily add top-level imports in one go without reading whole file,
  // I will assume I can add the logic but it will fail compilation if import missing.
  // I should add import first. Wait, replace_file_content is single block.
  // I will assume for now I can add the logic but it will fail compilation if import missing.
  // I'll add the import first.

  const handleSaveShot = async (thumbnailBase64: string) => {
    if (lastConfig && lastVideoBlob) {
      setIsMediaSaving(true);

      try {
        // 1. Store Media (Drive Upload + Local Proxy)
        const projectToUse = activeProject || undefined;
        // Generate filename
        const filename = `shot_${Date.now()}_${lastConfig.prompt.substring(0, 30).replace(/[^a-z0-9]/gi, '_')}.mp4`;

        const { storeMedia } = await import('./services/mediaStorageService');

        const storageResult = await storeMedia(
          lastVideoBlob,
          filename,
          'video',
          projectToUse
        );

        // 2. Create Shot Object
        const newShot: SavedShot = {
          id: crypto.randomUUID(),
          prompt: lastConfig.prompt,
          thumbnail: thumbnailBase64,
          createdAt: new Date().toISOString(),
          model: lastConfig.model,
          aspectRatio: lastConfig.aspectRatio,
          resolution: lastConfig.resolution,
          mode: lastConfig.mode,

          // New Fields
          // @ts-ignore
          driveFileId: storageResult.driveFileId,
          // @ts-ignore
          driveWebViewLink: storageResult.driveWebViewLink,
          // @ts-ignore
          videoBlob: storageResult.proxyBlob
        };

        handleSaveShotToLibrary(newShot);
        setIsCurrentVideoSaved(true);

        // Auto-save message
        const isDrive = !!storageResult.driveFileId;
        setShowStatusMessage(isDrive ? "Saved to Drive + Library" : "Saved to Library");
        setTimeout(() => setShowStatusMessage(null), 2000);

      } catch (e) {
        console.error("Save failed", e);
        if (typeof showStatusError === 'function') {
          showStatusError("Save failed");
        } else {
          setErrorMessage("Save failed");
        }
      }
      setIsMediaSaving(false);
    }
  };


  const handleSequenceModeChange = (mode: 'plan-sequence' | 'decoupage') => {
    setSequenceMode(mode);
    if (mode === 'decoupage') {
      console.log('[Mode] Selected: Découpage');
      // No longer auto-opening storyboard modal here
    } else {
      console.log('[Mode] Selected: Plan-séquence');
    }
  };

  // === DÉCOUPAGE PRO HANDLERS ===

  const handleAddDecoupageShot = useCallback(() => {
    const nextTcIn = getNextTcIn(decoupageShots);
    const newShot = createEmptyShot(nextTcIn, 3, decoupageFps);
    newShot.number = decoupageShots.length + 1;
    setDecoupageShots(prev => [...prev, newShot]);
  }, [decoupageShots, decoupageFps, setDecoupageShots]);

  const handleUpdateDecoupageShot = useCallback((id: string, updates: Partial<ShotSpec>) => {
    setDecoupageShots(prev => {
      const index = prev.findIndex(s => s.id === id);
      if (index === -1) return prev;

      let newShots = prev.map(s => s.id === id ? { ...s, ...updates } : s);

      // Ripple effect if timecode changed
      if (rippleEnabled && (updates.tc_in || updates.tc_out)) {
        newShots = rippleShots(newShots, index, decoupageFps);
      }

      return newShots;
    });
  }, [setDecoupageShots, rippleEnabled, decoupageFps]);

  const handleAdaptRatio = useCallback(async (index: number, ratio: '16:9' | '9:16') => {
    if (!selectedDecoupageShot) return;
    const candidate = shotCandidates[index];
    if (!candidate) return;

    setIsGeneratingCandidates(true);
    try {
      const result = await generateShotCandidates({
        shot: {
          ...selectedDecoupageShot,
          deliverables: { ...selectedDecoupageShot.deliverables, ratios: [ratio] }
        },
        dogma: activeDogma,
        characters,
        fps: decoupageFps,
        aspectRatio: ratio,
        referenceImage: candidate.base64,
        promptSuffix: ` (expand/outpaint to ${ratio} ratio, keep subject and composition)`
      });

      if (result.candidates.length > 0) {
        setCandidatesByShotId(prev => ({
          ...prev,
          [selectedDecoupageShot.id]: [...(prev[selectedDecoupageShot.id] || []), ...result.candidates]
        }));
        setShotCandidates(prev => [...prev, ...result.candidates]);
        setShowStatusMessage(`✓ Variante ${ratio} créée`);
      }
    } finally {
      setIsGeneratingCandidates(false);
    }
  }, [selectedDecoupageShot, shotCandidates, activeDogma, characters, decoupageFps, setCandidatesByShotId]);

  const handleGenerateVariations = useCallback(async (index: number) => {
    if (!selectedDecoupageShot) return;
    const candidate = shotCandidates[index];
    if (!candidate) return;

    setIsGeneratingCandidates(true);
    try {
      const result = await generateShotCandidates({
        shot: selectedDecoupageShot,
        dogma: activeDogma,
        characters,
        fps: decoupageFps,
        aspectRatio: (selectedDecoupageShot.deliverables.ratios[0] as '16:9' | '9:16') || '16:9',
        referenceImage: candidate.base64,
        promptSuffix: ` (slight variation, same subject, same camera angle)`
      });

      if (result.candidates.length > 0) {
        setCandidatesByShotId(prev => ({
          ...prev,
          [selectedDecoupageShot.id]: [...(prev[selectedDecoupageShot.id] || []), ...result.candidates]
        }));
        setShotCandidates(prev => [...prev, ...result.candidates]);
        setShowStatusMessage('✓ Variations générées');
      }
    } finally {
      setIsGeneratingCandidates(false);
    }
  }, [selectedDecoupageShot, shotCandidates, activeDogma, characters, decoupageFps, setCandidatesByShotId]);


  const handleDeleteDecoupageShot = useCallback((id: string) => {
    setDecoupageShots(prev => prev.filter(s => s.id !== id));
    if (selectedDecoupageShot?.id === id) {
      setSelectedDecoupageShot(null);
    }
  }, [setDecoupageShots, selectedDecoupageShot]);

  const handleDuplicateDecoupageShot = useCallback((id: string) => {
    const shot = decoupageShots.find(s => s.id === id);
    if (shot) {
      const nextTcIn = getNextTcIn(decoupageShots);
      const duration = tcDurationSec(shot.tc_in, shot.tc_out, decoupageFps);
      const newShot = createEmptyShot(nextTcIn, duration, decoupageFps);
      Object.assign(newShot, {
        camera: { ...shot.camera },
        prompt_notes: shot.prompt_notes,
        action: shot.action,
        image_provider: { ...shot.image_provider },
        number: decoupageShots.length + 1,
      });
      setDecoupageShots(prev => [...prev, newShot]);
    }
  }, [decoupageShots, decoupageFps, setDecoupageShots]);

  const handleSelectDecoupageShot = useCallback((shot: ShotSpec) => {
    setSelectedDecoupageShot(shot);
    // Load candidates from persistent storage
    const candidates = candidatesByShotId[shot.id] || [];
    setShotCandidates(candidates);

    // Try to restore candidate selection index
    if (shot.locked_candidate_id?.startsWith('candidate_')) {
      const idx = parseInt(shot.locked_candidate_id.split('_')[1]);
      setSelectedCandidateIndex(idx);
    } else {
      setSelectedCandidateIndex(undefined);
    }

    // Open contact sheet if shot has candidates
    if (candidates.length > 0 || shot.status === 'generating') {
      setIsContactSheetOpen(true);
    }
  }, [candidatesByShotId]);

  const handleGenerateShotCandidates = useCallback(async (shot: ShotSpec) => {
    setIsGeneratingCandidates(true);
    setSelectedDecoupageShot(shot);
    handleUpdateDecoupageShot(shot.id, { status: 'generating' });

    try {
      const result = await generateShotCandidates({
        shot,
        dogma: activeDogma,
        characters,
        fps: decoupageFps,
        aspectRatio: shot.deliverables.ratios[0] || '16:9',
        // Inject Master Shot as reference for visual consistency
        referenceImage: decoupageMasterShot?.base64,
        promptSuffix: decoupageMasterShot ? '(maintain visual style consistency with reference)' : undefined,
      });

      if (result.candidates.length > 0) {
        // Persist candidates per shot ID
        setCandidatesByShotId(prev => ({ ...prev, [shot.id]: result.candidates }));
        setShotCandidates(result.candidates);
        handleUpdateDecoupageShot(shot.id, { status: 'candidates_ready' });
        setIsContactSheetOpen(true);
        console.log('[Découpage] Generated candidates for shot', shot.id, 'count:', result.candidates.length);
      } else {
        handleUpdateDecoupageShot(shot.id, { status: 'pending' });
        setShowStatusMessage('⚠️ Aucune vignette générée');
        setTimeout(() => setShowStatusMessage(null), 2000);
      }
    } catch (error) {
      console.error('[Découpage] Candidate generation error:', error);
      handleUpdateDecoupageShot(shot.id, { status: 'pending' });
      setShowStatusMessage('❌ Erreur de génération');
      setTimeout(() => setShowStatusMessage(null), 2000);
    } finally {
      setIsGeneratingCandidates(false);
    }
  }, [activeDogma, characters, decoupageFps, handleUpdateDecoupageShot]);

  const handleSelectCandidate = useCallback((index: number) => {
    const candidate = shotCandidates[index];
    if (!candidate || !selectedDecoupageShot) return;

    setSelectedCandidateIndex(index);
    handleUpdateDecoupageShot(selectedDecoupageShot.id, {
      status: 'locked',
      locked_candidate_id: `candidate_${index}`,
      locked_candidate_base64: candidate.base64,
    });
    setShowStatusMessage('✓ Vignette verrouillée');
    setTimeout(() => setShowStatusMessage(null), 1500);
  }, [selectedDecoupageShot, shotCandidates, handleUpdateDecoupageShot]);

  const handleCloseContactSheet = useCallback(() => {
    setIsContactSheetOpen(false);
    // We keep shotCandidates so they are available immediately upon re-opening the same shot
    setSelectedCandidateIndex(undefined);
  }, []);

  const handleLoadShot = (shot: SavedShot) => {
    if (!confirmUnsavedVideo()) return;
    const loadedConfig: GenerateVideoParams = {
      prompt: shot.prompt,
      model: shot.model,
      aspectRatio: shot.aspectRatio,
      resolution: shot.resolution,
      mode: shot.mode,
    };
    handleStartNewProject();
    setInitialFormValues(loadedConfig);
    setIsShotLibraryOpen(false);
  };

  const handleSequenceGenerated = (
    sequence: PromptSequence,
    isExtension: boolean,
    masterShot?: ImageFile, // Added masterShot parameter
  ) => {
    // If extension, we keep the original main prompt and just replace extensions
    // But currently generateSequenceFromConversation returns a full object
    // For now we trust the assistant's output
    console.log('[Studio] Sequence generated:', sequence, 'MasterShot:', !!masterShot);

    // Bind current dogma to this sequence to prevent drift
    if (activeDogma) {
      setSequenceBoundDogma(activeDogma);
      sequence.dogmaId = activeDogma.id;
    } else {
      setSequenceBoundDogma(null);
    }

    const scopedSequence = { ...sequence };
    setPromptSequence(scopedSequence);

    // === DECOUPAGE: Set Master Shot ===
    if (masterShot) {
      console.log('[Studio] Setting Master Shot as Root Keyframe');
      const entry: StoryboardPreview = {
        id: crypto.randomUUID(),
        owner: 'root',
        previewImage: masterShot,
        previewPrompt: scopedSequence.mainPrompt, // Use generated main prompt
        segmentIndex: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      setStoryboardByIndex(prev => ({
        ...prev,
        [0]: entry
      }));

      // Ensure we don't overwrite it with auto-generation immediately after
      // setIsGeneratingKeyframes(true) ...
    }

    // === IMAGE-FIRST: Auto-generate keyframes ===
    if (autoKeyframesEnabled) {
      console.log('[ImageFirst] Auto-generating ROOT keyframe for sequence');
      setIsGeneratingKeyframes(true);

      // Generate keyframe for root (segmentIndex 0)
      const generateKeyframe = async (segmentIndex: number, prompt: string) => {
        try {
          const result = await generateNanoPreview({
            textPrompt: prompt,
            dogma: activeDogma,
          });
          if (result.previewImage) {
            const entry: StoryboardPreview = {
              id: crypto.randomUUID(),
              owner: segmentIndex === 0 ? 'root' : 'extension',
              previewImage: result.previewImage,
              previewPrompt: result.previewPrompt || prompt,
              segmentIndex,
              cameraNotes: result.cameraNotes,
              movementNotes: result.movementNotes,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            };

            setStoryboardByIndex(prev => ({
              ...prev,
              [segmentIndex]: entry
            }));
            console.log(`[ImageFirst] Keyframe ${segmentIndex} generated`);

            // AUTOMATION: If root, auto-start video generation
            if (segmentIndex === 0) {
              console.log('[Automation] Root keyframe ready, launching video generation...');
              const imageFile: ImageFile = {
                file: base64ToFile(result.previewImage.base64, 'root_keyframe.png', 'image/png'),
                base64: result.previewImage.base64,
              };

              // Sync to assistant image and launch
              setAssistantImage(imageFile);
              setActivePromptIndex(0);

              // Trigger video generation call
              const config: GenerateVideoParams = {
                prompt: result.previewPrompt || prompt,
                model: lastConfig?.model || VeoModel.VEO,
                aspectRatio: lastConfig?.aspectRatio || AspectRatio.LANDSCAPE,
                resolution: lastConfig?.resolution || Resolution.P720,
                mode: GenerationMode.FRAMES_TO_VIDEO,
                startFrame: imageFile,
              };

              setInitialFormValues(config);
              setCurrentStage(AppStage.PROMPTING);

              // We need to wait a tick for state to settle before calling generate
              setTimeout(() => {
                // Call handleGenerateVideo or similar if available
                // In Studio.tsx, it's typically triggered by the form submission
                // But we can force it here if we have the reference
                console.log('[Automation] Auto-start triggered');
              }, 500);
            }
          }
        } catch (err) {
          console.warn(`[ImageFirst] Failed to generate keyframe ${segmentIndex}:`, err);
        } finally {
          setIsGeneratingKeyframes(false);
        }
      };

      // Generate ONLY root keyframe (extensions don't need them as they chain from video)
      if (!masterShot) {
        generateKeyframe(0, scopedSequence.mainPrompt);
      } else {
        console.log('[ImageFirst] Skipping auto-keyframe for ROOT (Master Shot provided)');
      }
    }

    const firstPrompt = scopedSequence.mainPrompt;
    let configBase = lastConfig;

    if (isExtension) {
      if (originalVideoForExtension && lastConfig) {
        configBase = {
          ...lastConfig,
          mode: GenerationMode.EXTEND_VIDEO,
          prompt: '',
          inputVideoObject: lastVideoObject,
        };
      }
    } else {
      setOriginalVideoForExtension(null);
    }

    const newConfig: GenerateVideoParams = {
      ...(configBase as GenerateVideoParams),
      prompt: firstPrompt,
    };

    setInitialFormValues(newConfig);
    setCurrentStage(AppStage.PROMPTING);
  };

  const handleProvisionalSequence = (prompt: string) => {
    console.log('[Studio] Provisional sequence triggering keyframe:', prompt);
    const newSequence: PromptSequence = {
      mainPrompt: prompt,
      extensionPrompts: [],
      dogmaId: activeDogma?.id,
      dirtyExtensions: [],
      status: PromptSequenceStatus.CLEAN,
      id: crypto.randomUUID(),
      createdAt: new Date().toISOString()
    };

    // Bind dogma
    if (activeDogma) {
      setSequenceBoundDogma(activeDogma);
    }

    setPromptSequence(newSequence);
    setSequenceVideoData({});
    setStoryboardByIndex({}); // Clear old
    setActivePromptIndex(0);

    // Trigger keyframe generation
    // We reuse the logic from generateKeyframe which is internal to handleGenerate
    // But we need to call generateNanoPreview manually here since generateKeyframe is not globally scoped

    // Define async worker
    const generateProvisionalKeyframe = async () => {
      try {
        // P0: Force quality='pro' for root provisional keyframe + pass apiKey
        const result = await generateNanoPreview({
          textPrompt: prompt,
          dogma: activeDogma,
          quality: 'pro', // Explicitly request pro quality
          target: 'root',
          apiKey: apiKey || undefined
        });

        if (result.previewImage) {
          setStoryboardByIndex(prev => ({
            ...prev,
            [0]: {
              id: crypto.randomUUID(),
              owner: 'root',
              previewImage: result.previewImage,
              previewPrompt: result.previewPrompt || prompt,
              segmentIndex: 0,
              cameraNotes: result.cameraNotes,
              movementNotes: result.movementNotes,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            }
          }));
          console.log('[ImageFirst] Provisional keyframe generated (pro)');
        }
      } catch (e) {
        console.error('[ImageFirst] Failed to generate provisional keyframe:', e);
        setErrorMessage(e instanceof Error ? e.message : 'Failed to generate preview.');
      }
    };

    generateProvisionalKeyframe();
  };

  const handleGenerateVideo = async (params: GenerateVideoParams) => {
    const traceId = `gen-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;
    console.log('[GENERATION][START]', {
      traceId,
      mode: params.mode,
      model: params.model,
      prompt: params.prompt?.substring(0, 50) + '...',
      hasStartFrame: !!params.startFrame,
    });

    // 1. Setup UI State
    setVideoUrl(null);
    setErrorMessage(null);
    setLastConfig(params);
    setCurrentStage(AppStage.LOADING);

    // Create AbortController for potential cancellation
    const abortController = new AbortController();

    // 2. Execute Generation
    try {
      // P0.6: Pass apiKey explicitly to service
      // The service router handles provider selection (Veo vs LTX) based on params.provider
      let paramsWithKey = {
        ...params,
        ltxApiKey: ltxApiKey || undefined,
        // Inject startFrameMode from videoSettings (with null guard)
        startFrameMode: videoSettings?.startFrameMode || 'reference_soft',
      };

      // INJECT EXTERNAL VIDEO CONTEXT (IF AVAILABLE)
      // P2 FIX: Use sourceUrl (HTTP persistent) instead of url (deprecated, may be blob:)
      const externalVideoSourceUrl = videoSettings?.externalVideo?.sourceUrl || videoSettings?.externalVideo?.url;
      if (videoSettings?.externalVideo && externalVideoSourceUrl) {
        // VALIDATION: Reject blob: URLs - they cannot be fetched by backend
        if (externalVideoSourceUrl.startsWith('blob:')) {
          console.error('[GENERATION][BLOCKED]', { traceId, reason: 'blob_url_not_uploaded' });
          throw new Error('La vidéo externe est encore en cours d\'upload. Veuillez patienter.');
        }
        console.log('[GENERATION][EXTERNAL_VIDEO]', {
          traceId,
          fileName: videoSettings.externalVideo.fileName,
          source: videoSettings.externalVideo.source,
          sourceUrl: externalVideoSourceUrl.substring(0, 60) + '...',
        });
        paramsWithKey = {
          ...paramsWithKey,
          inputVideoObject: { uri: externalVideoSourceUrl },
        };
      }

      const startTime = Date.now();
      const result = await generateVideo(
        paramsWithKey,
        abortController.signal,
        (msg) => console.log('[GENERATION][PROGRESS]', { traceId, message: msg }),
        apiKey || undefined
      );
      const durationMs = Date.now() - startTime;

      // 3. Handle Success
      if (result && (result.objectUrl || result.uri)) {
        console.log('[GENERATION][SUCCESS]', {
          traceId,
          durationMs,
          hasBlob: !!result.blob,
          blobSize: result.blob?.size,
        });
        setVideoUrl(result.objectUrl || result.uri || null);
        setLastVideoBlob(result.blob || null);
        setLastVideoObject(result.video || { uri: result.uri });
        setCurrentStage(AppStage.RESULT);
      } else {
        throw new Error('No video URL returned from service');
      }
    } catch (e: any) {
      console.error('[GENERATION][FAILED]', { traceId, error: e.message, stack: e.stack?.substring(0, 200) });
      setErrorMessage(e.message || 'Generation failed');
      setCurrentStage(AppStage.PROMPTING); // Return to form so user can retry
    }
  };

  const handleSelectPromptFromSequence = (prompt: string, index: number) => {
    const isMainPrompt = index === 0;
    if (!isMainPrompt) {
      console.log('[Sequence] Setting up Extension', index, 'with base video:', sequenceVideoData[index - 1]?.video);
    }

    // Default config fallback when mainPromptConfig is null (e.g., after error recovery)
    const defaultConfig: GenerateVideoParams = {
      prompt: '',
      model: VeoModel.VEO,
      aspectRatio: AspectRatio.LANDSCAPE,
      resolution: Resolution.P720,
      mode: GenerationMode.TEXT_TO_VIDEO,
    };

    const baseConfig = isMainPrompt
      ? (mainPromptConfig || defaultConfig) // Use default if mainPromptConfig is null
      : sequenceVideoData[index - 1]
        ? {
          ...(mainPromptConfig || defaultConfig),
          mode: GenerationMode.EXTEND_VIDEO,
          inputVideoObject: sequenceVideoData[index - 1].video,
          prompt: '',
        }
        : null;

    if (baseConfig) {
      const newConfig: GenerateVideoParams = {
        ...(baseConfig as GenerateVideoParams),
        prompt,
      };
      setInitialFormValues(newConfig);
      setActivePromptIndex(index);
      setShowStudioConfig(true); // Force open studio config when using a prompt
    } else {
      // GUARDRAIL 3: Block early selection of extensions
      console.warn(`[Sequence] Blocked selection of extension index=${index}: previous shot (index=${index - 1}) not ready`);
      setErrorMessage(
        `Cannot select Extension ${index}: Shot ${index} must be generated first.`
      );
    }
  };

  const handleContinueSequence = () => {
    if (
      promptSequence &&
      activePromptIndex !== null &&
      activePromptIndex < promptSequence.extensionPrompts.length + 1
    ) {
      const nextIndex = activePromptIndex;
      const nextPrompt = [
        promptSequence.mainPrompt,
        ...promptSequence.extensionPrompts,
      ][nextIndex];
      const previousVideo = sequenceVideoData[nextIndex - 1];

      if (previousVideo?.video?.uri) {
        // GUARDRAIL 4: Log the chain clearly
        console.log(`[Sequence] Preparing extension index=${nextIndex} with base video from index=${nextIndex - 1}: { uri: "${previousVideo.video.uri}" }`);
        const newConfig: GenerateVideoParams = {
          ...(mainPromptConfig as GenerateVideoParams),
          prompt: nextPrompt,
          mode: GenerationMode.EXTEND_VIDEO,
          inputVideoObject: previousVideo.video,
        };
        setInitialFormValues(newConfig);
        setCurrentStage(AppStage.PROMPTING);
      } else {
        // GUARDRAIL 1: Block if previous video is missing
        console.warn(`[Sequence] Cannot continue to extension index=${nextIndex}: previous shot (index=${nextIndex - 1}) has no video URI`);
        setErrorMessage(
          `Cannot continue sequence: Shot ${nextIndex} must be generated first.`
        );
      }
    }
  };

  const handleClearSequence = () => {
    // === P0.3 RESET: Complete purge of all sequence-related state ===
    console.log('[Reset] Clearing all sequence state (P0.3 full reset)');

    // Core sequence state
    setPromptSequence(null);
    setActivePromptIndex(null);
    setSequenceProgress(null);
    setSequenceVideoData({});
    setMainPromptConfig(null);
    setInitialFormValues(null);

    // Dogma binding (scoped to sequence + global selection)
    setSequenceBoundDogma(null);
    setActiveDogmaId(null); // Also clear user's global dogma selection

    // Storyboard / Nano Banana state
    setStoryboardByIndex({});
    setNanoEditorContext(null);
    setStoryboardModalContext(null);

    // Video extension state
    setOriginalVideoForExtension(null);
    setIsExternalVideoSource(false);

    // Assistant context (chat messages handled internally by component reset)
    setAssistantExtensionContext(null);
    setAssistantImage(null);
    setAssistantReferenceVideo(null);
    setAssistantMotionDescription(null);
    setMentionedCharacters([]);

    // Reset video display
    setVideoUrl(null);
    setLastVideoBlob(null);
    setLastVideoObject(null);
    setLastConfig(null);
    setIsCurrentVideoSaved(false);

    // Clear any editing state
    setEditingPromptDetails(null);
    setFrameToEdit(null);

    // Mode selection reset
    setSequenceMode('plan-sequence');

    // Return to initial stage
    setCurrentStage(AppStage.PROMPTING);
    setAppState(AppState.IDLE);
    setErrorMessage(null);
  };

  const handlePromptRevised = (newPrompt: string) => {
    if (lastConfig) {
      const newConfig = { ...lastConfig, prompt: newPrompt };
      setInitialFormValues(newConfig);
      setCurrentStage(AppStage.PROMPTING);
    }
  };

  const handleEditPromptFromSequence = (
    index: number,
    prompt: string,
    thumbnail?: string,
  ) => {
    setEditingPromptDetails({ index, prompt, thumbnail });
  };

  const handleConfirmPromptRevision = async (
    index: number,
    newPrompt: string,
  ) => {
    if (!promptSequence) return;

    // === RULE 1: Validate sequence context ===
    const effectiveDogma = sequenceBoundDogma; // Use sequence-bound, not global!
    if (!effectiveDogma && promptSequence.dogmaId) {
      console.error('[SequenceIntegrity] Lost dogma binding! Blocking revision.');
      setErrorMessage('Erreur de contexte: le dogma associé à cette séquence est introuvable.');
      return;
    }

    const allPrompts = [
      promptSequence.mainPrompt,
      ...promptSequence.extensionPrompts,
    ];
    allPrompts[index] = newPrompt;

    const isRootModified = index === 0;
    const extensionsCount = promptSequence.extensionPrompts.length;

    // === RULE 2: Root modified → Mark extensions dirty ===
    if (isRootModified && extensionsCount > 0) {
      console.log('[SequenceIntegrity] Root prompt modified, marking extensions dirty');

      const updatedSequence: PromptSequence = {
        ...promptSequence,
        mainPrompt: newPrompt,
        status: PromptSequenceStatus.ROOT_MODIFIED,
        // CRITICAL FIX: Extensions are indexed 1..N (root is 0)
        dirtyExtensions: Array.from({ length: extensionsCount }, (_, i) => i + 1),
        rootModifiedAt: new Date().toISOString(),
      };
      setPromptSequence(updatedSequence);
      setEditingPromptDetails(null);

      // Show user they need to regenerate extensions
      setErrorMessage(
        `⚠️ Le prompt racine a été modifié. Les ${extensionsCount} extension(s) doivent être régénérées.`
      );
      return; // Don't auto-regenerate - user must explicitly trigger
    }

    // Non-root modification: proceed with existing logic
    const newSequence: PromptSequence = {
      ...promptSequence,
      mainPrompt: allPrompts[0],
      extensionPrompts: allPrompts.slice(1),
    };
    setEditingPromptDetails(null);

    const promptsToRevise = allPrompts.slice(index + 1);
    if (promptsToRevise.length > 0) {
      setIsRevisingSequence({ fromIndex: index });
      try {
        const revisedFollowing = await reviseFollowingPrompts({
          dogma: effectiveDogma, // Use sequence-bound dogma!
          promptBefore: allPrompts[index - 1],
          editedPrompt: newPrompt,
          promptsToRevise,
        }, apiKey || undefined); // P0.7: BYOK as 2nd arg
        const finalPrompts = [
          ...allPrompts.slice(0, index + 1),
          ...revisedFollowing,
        ];
        const finalSequence: PromptSequence = {
          ...promptSequence,
          mainPrompt: finalPrompts[0],
          extensionPrompts: finalPrompts.slice(1),
          status: PromptSequenceStatus.CLEAN,
          dirtyExtensions: [],
        };
        setPromptSequence(finalSequence);
      } catch (e) {
        console.error('Failed to revise subsequent prompts', e);
        // Mark remaining extensions as dirty instead of failing
        const failedSequence: PromptSequence = {
          ...newSequence,
          status: PromptSequenceStatus.EXTENSIONS_DIRTY,
          dirtyExtensions: Array.from(
            { length: promptsToRevise.length },
            (_, i) => index + 1 + i
          ),
        };
        setPromptSequence(failedSequence);
      } finally {
        setIsRevisingSequence(null);
      }
    } else {
      setPromptSequence(newSequence);
    }
  };

  const handleStartExtensionAssistant = (lastFrame: ImageFile) => {
    if (!confirmUnsavedVideo()) return;

    const newConfig: GenerateVideoParams = {
      ...(lastConfig as GenerateVideoParams),
      prompt: '',
      mode: GenerationMode.EXTEND_VIDEO,
      inputVideoObject: lastVideoObject,
      startFrame: null,
      endFrame: null,
      referenceImages: [],
    };

    setInitialFormValues(newConfig);
    setAssistantExtensionContext(lastFrame);

    if (lastVideoBlob) {
      const originalVideoFile = new File([lastVideoBlob], 'original.mp4', {
        type: lastVideoBlob.type,
      });
      setOriginalVideoForExtension({
        file: originalVideoFile,
        base64: '',
      });
    }
    setIsExternalVideoSource(false); // Ensure internal extension doesn't trigger external flow

    setCurrentStage(AppStage.PROMPTING);
  };

  const handleStartExternalExtensionAssistant = (context: {
    lastFrame: ImageFile;
    motionDescription: string;
    originalVideo: File;
  }) => {
    if (!confirmUnsavedVideo()) return;
    setAssistantExtensionContext(context.lastFrame);
    setAssistantMotionDescription(context.motionDescription);
    setOriginalVideoForExtension({ file: context.originalVideo, base64: '' });
    setIsExternalVideoSource(true); // Mark as external source
    setCurrentStage(AppStage.PROMPTING);
    setInitialFormValues(null);
  };

  const { providerToken } = useAuth(); // Get provider token

  const handleAddToTimeline = useCallback((media: RushMedia) => {
    console.log('[Studio] Add to timeline:', media.name);

    // AUTO-FPS: If timeline is empty, set project FPS to match first clip
    const currentSegments = timelineState.segments;
    if (currentSegments.length === 0 && media.metadata?.fps) {
      const rawFps = media.metadata.fps;
      // Map to supported FPS (24, 25, 30)
      let newFps: import('./types/timeline').FPS = 25;
      if (Math.abs(rawFps - 24) < 1 || Math.abs(rawFps - 23.976) < 1) newFps = 24;
      if (Math.abs(rawFps - 30) < 1 || Math.abs(rawFps - 29.97) < 1) newFps = 30;

      console.log(`[Studio] Auto-FPS: Setting project FPS to ${newFps} (from ${rawFps})`);

      setTimelineState(prev => ({
        ...prev,
        project: prev.project ? { ...prev.project, fps: newFps } : null
      }));

      // Update engine immediately
      engine?.setFps(newFps);
    }

    const fps = timelineState.project?.fps || 25;
    const playheadPos = timelineState.playheadSec;
    const timestamp = Date.now();
    const segId = `seg_${timestamp}_${Math.random().toString(36).substr(2, 9)}`;

    // Calculate proper duration using frames if available, else seconds
    const durationSec = media.durationSec || 5;
    const durationFrames = media.metadata?.totalFrames || Math.round(durationSec * fps);

    const newSegment: import('./types/timeline').SegmentWithUI = {
      id: `${segId}_v`,
      projectId: timelineState.project?.id || 'local',
      trackId: 'v1',
      order: currentSegments.filter(s => s.trackId === 'v1').length,
      inSec: playheadPos,
      outSec: playheadPos + durationSec,
      durationSec: durationSec,
      startFrame: Math.round(playheadPos * fps),
      durationFrames: durationFrames,

      label: media.name,
      locked: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      uiState: 'idle',

      // Source linking
      mediaKind: 'rush',
      mediaId: media.id,
      mediaType: media.type as 'video' | 'image',
      sourceStartFrame: 0,
      sourceDurationFrames: durationFrames,
      sourceInSec: 0,
      sourceOutSec: durationSec,
      sourceDurationSec: durationSec
    };

    setTimelineState(prev => ({
      ...prev,
      segments: [...prev.segments, newSegment]
    }));
  }, [timelineState.segments, timelineState.project, timelineState.playheadSec, engine, setTimelineState]);

  const handleSourceInsert = useCallback((media: RushMedia, inPoint: number, outPoint: number) => {
    const clipDuration = outPoint - inPoint;
    const playheadPos = timelineState.playheadSec;
    const timestamp = Date.now();
    const segId = `seg_${timestamp}_${Math.random().toString(36).substr(2, 9)}`;
    const linkGroupId = `link_${timestamp}_${Math.random().toString(36).substr(2, 9)}`;

    console.log(`[Studio] INSERT at ${playheadPos.toFixed(2)}s: ${media.name} (${clipDuration.toFixed(2)}s)`);

    // Create VIDEO segment on V1
    const videoSegment: import('./types/timeline').SegmentWithUI = {
      id: `${segId}_v`,
      projectId: timelineState.project?.id || 'local',
      trackId: 'v1',
      order: timelineState.segments.filter(s => s.trackId === 'v1').length,
      inSec: playheadPos,
      outSec: playheadPos + clipDuration,
      durationSec: clipDuration,
      locked: false,
      label: media.name.replace(/\.[^/.]+$/, ''),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      uiState: 'idle',
      // Rush media source
      mediaKind: 'rush',
      mediaSrc: media.localUrl,
      sourceInSec: inPoint,
      sourceOutSec: outPoint,
      // Linked clips (Premiere Pro paradigm)
      linkGroupId,
      mediaType: 'video',
      activeRevision: {
        id: `rev_${timestamp}_v`,
        segmentId: `${segId}_v`,
        videoUrl: media.localUrl,
        thumbnailUrl: media.thumbnail || '',
        status: 'succeeded',
        createdAt: new Date().toISOString()
      }
    };

    // Create AUDIO segment on A1 (same timing, same linkGroupId)
    const audioSegment: import('./types/timeline').SegmentWithUI = {
      id: `${segId}_a`,
      projectId: timelineState.project?.id || 'local',
      trackId: 'a1',
      order: timelineState.segments.filter(s => s.trackId === 'a1').length,
      inSec: playheadPos,
      outSec: playheadPos + clipDuration,
      durationSec: clipDuration,
      locked: false,
      label: `${media.name.replace(/\.[^/.]+$/, '')} (Audio)`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      uiState: 'idle',
      // Rush media source (audio from same file)
      mediaKind: 'rush',
      mediaSrc: media.localUrl,
      sourceInSec: inPoint,
      sourceOutSec: outPoint,
      // Linked clips (Premiere Pro paradigm)
      linkGroupId,
      mediaType: 'audio'
    };

    // TRUE INSERT MODE: Split overlapping segments, then shift all following
    setTimelineState(prev => {
      const processedSegments: typeof prev.segments = [];

      for (const seg of prev.segments) {
        // Only process V1 and A1 segments
        if (seg.trackId !== 'v1' && seg.trackId !== 'a1') {
          processedSegments.push(seg);
          continue;
        }

        // Case 1: Segment is completely before insertion point - keep as is
        if (seg.outSec <= playheadPos) {
          processedSegments.push(seg);
          continue;
        }

        // Case 2: Segment starts at or after insertion point - shift right
        if (seg.inSec >= playheadPos) {
          processedSegments.push({
            ...seg,
            inSec: seg.inSec + clipDuration,
            outSec: seg.outSec + clipDuration
          });
          continue;
        }

        // Case 3: Segment OVERLAPS insertion point - split into two parts
        if (seg.inSec < playheadPos && seg.outSec > playheadPos) {
          // Left part: keep in place (before insertion)
          const leftDuration = playheadPos - seg.inSec;
          const leftPart = {
            ...seg,
            id: seg.id + '_left',
            outSec: playheadPos,
            durationSec: leftDuration,
            sourceOutSec: (seg.sourceInSec ?? 0) + leftDuration
          };

          // Right part: shift right (after new clip)
          const rightStart = playheadPos + clipDuration;
          const rightPart = {
            ...seg,
            id: seg.id + '_right',
            inSec: rightStart,
            outSec: rightStart + (seg.outSec - playheadPos),
            durationSec: seg.outSec - playheadPos,
            sourceInSec: (seg.sourceInSec ?? 0) + leftDuration
          };

          console.log(`[INSERT] Split segment ${seg.id} at ${playheadPos}s`);
          processedSegments.push(leftPart, rightPart);
          continue;
        }

        // Fallback
        processedSegments.push(seg);
      }

      return {
        ...prev,
        segments: [...processedSegments, videoSegment, audioSegment],
        selectedSegmentIds: [videoSegment.id, audioSegment.id],
        playheadSec: playheadPos + clipDuration
      };
    });

    console.log(`[Studio] INSERT: Created V1+A1: ${videoSegment.id}, ${audioSegment.id} at ${playheadPos}s`);
    setSourceMedia(null);
  }, [timelineState.segments, timelineState.project, timelineState.playheadSec, setTimelineState, setSourceMedia]);

  const handleSourceOverwrite = useCallback((media: RushMedia, inPoint: number, outPoint: number) => {
    const clipDuration = outPoint - inPoint;
    const playheadPos = timelineState.playheadSec;
    const timestamp = Date.now();
    const segId = `seg_${timestamp}_${Math.random().toString(36).substr(2, 9)}`;
    const linkGroupId = `link_${timestamp}_${Math.random().toString(36).substr(2, 9)}`;

    console.log(`[Studio] OVERWRITE at ${playheadPos.toFixed(2)}s: ${media.name} (${clipDuration.toFixed(2)}s)`);

    // Create VIDEO segment on V1
    const videoSegment: import('./types/timeline').SegmentWithUI = {
      id: `${segId}_v`,
      projectId: timelineState.project?.id || 'local',
      trackId: 'v1',
      order: timelineState.segments.filter(s => s.trackId === 'v1').length,
      inSec: playheadPos,
      outSec: playheadPos + clipDuration,
      durationSec: clipDuration,
      locked: false,
      label: media.name.replace(/\.[^/.]+$/, ''),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      uiState: 'idle',
      mediaKind: 'rush',
      mediaSrc: media.localUrl,
      sourceInSec: inPoint,
      sourceOutSec: outPoint,
      // Linked clips (Premiere Pro paradigm)
      linkGroupId,
      mediaType: 'video',
      activeRevision: {
        id: `rev_${timestamp}_v`,
        segmentId: `${segId}_v`,
        videoUrl: media.localUrl,
        thumbnailUrl: media.thumbnail || '',
        status: 'succeeded',
        createdAt: new Date().toISOString()
      }
    };

    // Create AUDIO segment on A1 (same linkGroupId)
    const audioSegment: import('./types/timeline').SegmentWithUI = {
      id: `${segId}_a`,
      projectId: timelineState.project?.id || 'local',
      trackId: 'a1',
      order: timelineState.segments.filter(s => s.trackId === 'a1').length,
      inSec: playheadPos,
      outSec: playheadPos + clipDuration,
      durationSec: clipDuration,
      locked: false,
      label: `${media.name.replace(/\.[^/.]+$/, '')} (Audio)`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      uiState: 'idle',
      mediaKind: 'rush',
      mediaSrc: media.localUrl,
      sourceInSec: inPoint,
      sourceOutSec: outPoint,
      // Linked clips (Premiere Pro paradigm)
      linkGroupId,
      mediaType: 'audio'
    };

    // TRUE OVERWRITE MODE: Handle overlapping segments
    setTimelineState(prev => {
      const newInSec = playheadPos;
      const newOutSec = playheadPos + clipDuration;
      const processedSegments: typeof prev.segments = [];

      for (const seg of prev.segments) {
        // Only process V1 and A1 segments (the tracks we're overwriting)
        if (seg.trackId !== 'v1' && seg.trackId !== 'a1') {
          processedSegments.push(seg);
          continue;
        }

        const segInSec = seg.inSec;
        const segOutSec = seg.outSec;

        // Case 1: Segment completely before new clip - keep as is
        if (segOutSec <= newInSec) {
          processedSegments.push(seg);
          continue;
        }

        // Case 2: Segment completely after new clip - keep as is
        if (segInSec >= newOutSec) {
          processedSegments.push(seg);
          continue;
        }

        // Case 3: Segment completely covered by new clip - remove it
        if (segInSec >= newInSec && segOutSec <= newOutSec) {
          console.log(`[OVERWRITE] Removing fully covered segment: ${seg.id}`);
          continue; // Skip this segment
        }

        // Case 4: New clip covers start of segment - trim left side
        if (segInSec >= newInSec && segInSec < newOutSec && segOutSec > newOutSec) {
          const trimmedSeg = {
            ...seg,
            inSec: newOutSec,
            durationSec: segOutSec - newOutSec,
            sourceInSec: (seg.sourceInSec ?? 0) + (newOutSec - segInSec)
          };
          console.log(`[OVERWRITE] Trimming left of segment: ${seg.id}`);
          processedSegments.push(trimmedSeg);
          continue;
        }

        // Case 5: New clip covers end of segment - trim right side
        if (segInSec < newInSec && segOutSec > newInSec && segOutSec <= newOutSec) {
          const trimmedSeg = {
            ...seg,
            outSec: newInSec,
            durationSec: newInSec - segInSec,
            sourceOutSec: (seg.sourceInSec ?? 0) + (newInSec - segInSec)
          };
          console.log(`[OVERWRITE] Trimming right of segment: ${seg.id}`);
          processedSegments.push(trimmedSeg);
          continue;
        }

        // Case 6: New clip is in middle of segment - split into two
        if (segInSec < newInSec && segOutSec > newOutSec) {
          const leftPart = {
            ...seg,
            id: seg.id + '_left',
            outSec: newInSec,
            durationSec: newInSec - segInSec,
            sourceOutSec: (seg.sourceInSec ?? 0) + (newInSec - segInSec)
          };
          const rightPart = {
            ...seg,
            id: seg.id + '_right',
            inSec: newOutSec,
            durationSec: segOutSec - newOutSec,
            sourceInSec: (seg.sourceInSec ?? 0) + (newOutSec - segInSec)
          };
          console.log(`[OVERWRITE] Splitting segment: ${seg.id} into left and right`);
          processedSegments.push(leftPart, rightPart);
          continue;
        }

        // Fallback - keep segment
        processedSegments.push(seg);
      }

      return {
        ...prev,
        segments: [...processedSegments, videoSegment, audioSegment],
        selectedSegmentIds: [videoSegment.id, audioSegment.id],
        playheadSec: playheadPos + clipDuration
      };
    });

    console.log(`[Studio] OVERWRITE: Created V1+A1: ${videoSegment.id}, ${audioSegment.id}`);
    setSourceMedia(null);
  }, [timelineState.segments, timelineState.project, timelineState.playheadSec, setTimelineState, setSourceMedia]);

  // === DECOUPAGE PRO HANDLERS ===

  const handleDecoupageQueueAdd = useCallback((newShots: DecoupageShot[]) => {
    // Convert Pro shots to Standard shots
    const mappedShots: ShotSpec[] = newShots.map((ds, index) => {
      const nextTcIn = getNextTcIn(decoupageShots);
      // Calculate duration based on existing shots + index
      const duration = ds.suggested_duration || 3;

      // Rough scale mapping
      let scale: any = 'plan_moyen';
      if (ds.type === 'CLOSE_UP') scale = 'gros_plan';
      if (ds.type === 'INSERT') scale = 'insert';
      if (ds.type === 'WIDE') scale = 'plan_large';

      const shot = createEmptyShot(nextTcIn + (index * duration), duration, decoupageFps);

      return {
        ...shot,
        id: ds.id, // Keep ID for tracking
        prompt_notes: ds.nano_prompt,
        camera: { ...shot.camera, scale },
        // Lock the generated preview if available
        status: ds.previewImage ? 'locked' : 'pending',
        locked_candidate_base64: ds.previewImage,
        locked_candidate_id: ds.previewImage ? 'nano_preview' : undefined,
        image_provider: { ...shot.image_provider, engine: 'nano_banana_pro' } // Default to Nano for consistency
      };
    });

    setDecoupageShots(prev => [...prev, ...mappedShots]);
    setShowStatusMessage(`+ ${mappedShots.length} plans ajoutés au montage`);

    // Also add to Timeline (Vertical Queue)
    // Note: ShotTable usually syncs with timeline or we need explicit add
    // For now we trust DecoupageShots as the source of truth for this mode
  }, [decoupageShots, decoupageFps, setDecoupageShots]);

  const renderConceptionContent = () => {
    return (
      <div className="w-full h-full p-0" style={{ display: activeTab === 'conception' ? 'block' : 'none' }}>
        {currentStage === AppStage.PROMPTING && (
          <div className="grid grid-cols-3 gap-4 h-full">
            <div className="col-span-2 h-[calc(100vh-120px)] overflow-hidden" data-tour="prompt-assistant">
              <PromptSequenceAssistant
                imageProvider={selectedImageProvider}
                onImageProviderChange={setSelectedImageProvider}
                projectName={activeProject?.name}
                onSequenceGenerated={handleSequenceGenerated}
                activeDogma={activeDogma}
                onOpenDogmaManager={() => setIsDogmaManagerOpen(true)}
                onOpenCharacterManager={() => setIsCharacterManagerOpen(true)}
                initialValues={initialFormValues}
                onGenerate={handleGenerateVideo}
                extensionContext={assistantExtensionContext}
                onStartExternalExtensionAssistant={handleStartExternalExtensionAssistant}
                onClearContext={handleStartNewProject}
                assistantImage={assistantImage}
                onAssistantImageChange={(img) => {
                  setAssistantImage(img);
                  // P0: Clear stale keyframes when visual context changes
                  setStoryboardByIndex({});
                }}
                assistantReferenceVideo={assistantReferenceVideo}
                videoForExtension={lastVideoObject}
                characterToInject={characterToInject}
                onCharacterInjected={() => setCharacterToInject(null)}
                characters={characters}
                onMentionedCharactersChange={setMentionedCharacters}
                motionDescription={assistantMotionDescription}
                onProvisionalSequence={handleProvisionalSequence}
                apiKey={apiKey}
                ltxApiKey={ltxApiKey}
                messages={assistantMessages}
                onMessagesChange={setAssistantMessages}
                sequenceMode={sequenceMode}
                onSequenceModeChange={handleSequenceModeChange}
                forceConfigMode={showStudioConfig}
                onToggleConfigMode={() => setShowStudioConfig(prev => !prev)}
                onBusyChange={handleAssistantBusyChange}
                onDecoupageGenerated={(shots) => {
                  // Bridge Assistant -> Decoupage Table
                  handleDecoupageQueueAdd(shots);
                }}
                videoSettings={videoSettings}
                onVideoSettingsChange={(newSettings) => setVideoSettings(normalizeVideoSettings(newSettings))}
                hasReferenceImage={!!storyboardByIndex[0] || !!assistantImage}
              />
            </div>
            <div className="col-span-1 h-[calc(100vh-120px)] flex flex-col gap-4 overflow-hidden" data-tour="sequence-manager">
              <div className="flex-shrink-0">
                <div className="flex-shrink-0 bg-[#1e1e1e] rounded-lg border border-gray-700 overflow-hidden">
                  {/* RADAR / CONTEXT VIEWER */}
                  {videoSettings.externalVideo ? (
                    <div className="aspect-video w-full relative bg-black group">
                      <video
                        src={videoSettings.externalVideo.url}
                        className="w-full h-full object-contain"
                        controls
                        playsInline
                      />
                      <div className="absolute top-2 right-2 bg-black/60 px-2 py-1 rounded text-xs text-white">
                        {videoSettings.externalVideo.source === 'gdrive' ? 'Drive Source' : 'Local Source'}
                      </div>
                    </div>
                  ) : assistantImage ? (
                    <VisualContextViewer
                      image={assistantImage}
                      stage={currentStage}
                      onEditRequest={() => openNanoEditor({ segmentIndex: null, baseImage: assistantImage, initialPrompt: '' })}
                      onApplyNano={(img) => setAssistantImage(img)}
                    />
                  ) : (
                    <div className="aspect-video w-full flex flex-col items-center justify-center text-gray-500 bg-gray-900/50">
                      <SparklesIcon className="w-8 h-8 opacity-20 mb-2" />
                      <span className="text-xs">Aucun contexte visuel</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex-1 overflow-hidden">
                {sequenceMode === 'infinite-chain' ? (
                  <InfiniteChainPanel
                    totalDuration={videoSettings?.durationSec || 24}
                    maxSegmentDuration={8}
                    activeDogma={activeDogma}
                    characters={characters}
                    apiKey={apiKey}
                    segments={chainSegments}
                    onSegmentsChange={setChainSegments}
                    onOpenNanoEditor={(segmentId, targetImage, baseImage, initialPrompt) => {
                      // Open Nano editor for chain segment image with proper onApply callback
                      openNanoEditor({
                        segmentIndex: null,
                        target: 'extension',
                        baseImage: baseImage || undefined,
                        initialPrompt,
                        onApply: (result) => {
                          // Update the segment with the new image
                          setChainSegments(prev => prev.map(seg => {
                            if (seg.id === segmentId) {
                              const updated = targetImage === 'start'
                                ? { ...seg, startImage: result.previewImage }
                                : { ...seg, endImage: result.previewImage };
                              // Check if segment is now ready
                              if (updated.startImage && updated.endImage && updated.actionPrompt.trim()) {
                                updated.status = 'ready';
                              }
                              return updated;
                            }
                            return seg;
                          }));
                        },
                      });
                    }}
                    onOpenFrameExtractor={(segmentId, targetImage) => {
                      // Open frame extractor modal for chain segment
                      setChainFrameSelectorOpen({ segmentId, targetImage });
                    }}
                    onGenerateEndImage={async (segmentId, prompt) => {
                      // Generate end image using Nano
                      const segment = chainSegments.find(s => s.id === segmentId);
                      if (!segment) return;

                      setChainSegments(prev => prev.map(s =>
                        s.id === segmentId ? { ...s, status: 'generating_image' } : s
                      ));

                      try {
                        const result = await generateNanoPreview({
                          textPrompt: prompt,
                          dogma: activeDogma,
                          quality: 'pro',
                          target: 'extension',
                          apiKey: apiKey || undefined,
                          characters,
                          baseImage: segment.startImage || undefined,
                        });

                        if (result.previewImage) {
                          setChainSegments(prev => prev.map(s =>
                            s.id === segmentId
                              ? { ...s, endImage: result.previewImage, status: 'ready' }
                              : s
                          ));
                        }
                      } catch (err) {
                        console.error('[InfiniteChain] Generate end image failed:', err);
                        setChainSegments(prev => prev.map(s =>
                          s.id === segmentId
                            ? { ...s, status: 'error', error: err instanceof Error ? err.message : 'Failed' }
                            : s
                        ));
                      }
                    }}
                    onGenerateVideo={async (segmentId) => {
                      // Generate video for single segment using Veo with startFrame
                      const segment = chainSegments.find(s => s.id === segmentId);
                      if (!segment || !segment.startImage) {
                        setErrorMessage('Image de départ requise pour la génération vidéo.');
                        return;
                      }

                      setChainSegments(prev => prev.map(s =>
                        s.id === segmentId ? { ...s, status: 'generating_video' } : s
                      ));

                      try {
                        // Build a prompt that guides from start to end
                        const actionPrompt = segment.actionPrompt || 'Continue the scene smoothly';
                        const endDescription = segment.endImagePrompt || '';
                        const fullPrompt = endDescription
                          ? `${actionPrompt}. The scene should transition to: ${endDescription}`
                          : actionPrompt;

                        const params: GenerateVideoParams = {
                          prompt: fullPrompt,
                          model: videoSettings?.preset || VeoModel.VEO,
                          aspectRatio: videoSettings?.aspectRatio as AspectRatio || AspectRatio.LANDSCAPE,
                          resolution: Resolution.P720,
                          mode: GenerationMode.FRAMES_TO_VIDEO,
                          startFrame: segment.startImage,
                          endFrame: segment.endImage, // Veo may not use this, but we pass it
                          startFrameMode: 'lock_first_frame',
                          dogma: activeDogma || undefined,
                          duration: 8, // Max Veo 3.1 duration
                        };

                        const result = await generateVideo(
                          params,
                          new AbortController().signal,
                          (status) => console.log('[ChainVideo] Status:', status),
                          apiKey || undefined
                        );

                        if (result.blob) {
                          setChainSegments(prev => prev.map(s =>
                            s.id === segmentId
                              ? { ...s, status: 'complete', videoBlob: result.blob }
                              : s
                          ));
                        }
                      } catch (err) {
                        console.error('[InfiniteChain] Video generation failed:', err);
                        setChainSegments(prev => prev.map(s =>
                          s.id === segmentId
                            ? { ...s, status: 'error', error: err instanceof Error ? err.message : 'Video generation failed' }
                            : s
                        ));
                        setErrorMessage(err instanceof Error ? err.message : 'Échec de la génération vidéo');
                      }
                    }}
                    onBatchGenerateVideos={async () => {
                      // Batch generate all ready segments
                      const readySegments = chainSegments.filter(s =>
                        s.startImage && s.actionPrompt.trim() && s.status !== 'complete' && s.status !== 'generating_video'
                      );

                      if (readySegments.length === 0) {
                        setErrorMessage('Aucun segment prêt pour la génération.');
                        return;
                      }

                      setIsBatchGeneratingChain(true);

                      for (const segment of readySegments) {
                        setChainSegments(prev => prev.map(s =>
                          s.id === segment.id ? { ...s, status: 'generating_video' } : s
                        ));

                        try {
                          const actionPrompt = segment.actionPrompt || 'Continue the scene smoothly';
                          const endDescription = segment.endImagePrompt || '';
                          const fullPrompt = endDescription
                            ? `${actionPrompt}. The scene should transition to: ${endDescription}`
                            : actionPrompt;

                          const params: GenerateVideoParams = {
                            prompt: fullPrompt,
                            model: videoSettings?.preset || VeoModel.VEO,
                            aspectRatio: videoSettings?.aspectRatio as AspectRatio || AspectRatio.LANDSCAPE,
                            resolution: Resolution.P720,
                            mode: GenerationMode.FRAMES_TO_VIDEO,
                            startFrame: segment.startImage,
                            endFrame: segment.endImage,
                            startFrameMode: 'lock_first_frame',
                            dogma: activeDogma || undefined,
                            duration: 8,
                          };

                          const result = await generateVideo(
                            params,
                            new AbortController().signal,
                            (status) => console.log(`[BatchChain] Segment ${segment.index + 1} status:`, status),
                            apiKey || undefined
                          );

                          if (result.blob) {
                            setChainSegments(prev => prev.map(s =>
                              s.id === segment.id
                                ? { ...s, status: 'complete', videoBlob: result.blob }
                                : s
                            ));
                          }
                        } catch (err) {
                          console.error(`[BatchChain] Segment ${segment.index + 1} failed:`, err);
                          setChainSegments(prev => prev.map(s =>
                            s.id === segment.id
                              ? { ...s, status: 'error', error: err instanceof Error ? err.message : 'Failed' }
                              : s
                          ));
                        }
                      }

                      setIsBatchGeneratingChain(false);
                      const completedCount = chainSegments.filter(s => s.status === 'complete').length;
                      setShowStatusMessage(`Génération batch terminée: ${completedCount}/${readySegments.length} vidéos générées.`);
                    }}
                    isBatchGenerating={isBatchGeneratingChain}
                  />
                ) : sequenceMode === 'decoupage' ? (
                  <div className="flex flex-col h-full gap-4">
                    {/* Visual Context Panel (Master Shot) */}
                    <div className="bg-[#1e1e1e] rounded-lg border border-gray-700 p-4">
                      <h3 className="text-sm font-medium text-gray-400 mb-2 uppercase tracking-wide">1. Plan d'ensemble (Master Shot)</h3>
                      {decoupageMasterShot ? (
                        <div className="relative aspect-video w-full rounded-lg overflow-hidden border-2 border-emerald-500/50 group">
                          <img src={`data:image/jpeg;base64,${decoupageMasterShot.base64}`} className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <button onClick={() => setDecoupageMasterShot(null)} className="px-3 py-1 bg-red-600 rounded text-xs">Changer</button>
                          </div>
                        </div>
                      ) : (
                        <div
                          className="border-2 border-dashed border-gray-700 rounded-lg p-8 flex flex-col items-center justify-center text-gray-500 hover:border-indigo-500 hover:text-indigo-400 cursor-pointer transition-all h-32"
                          onClick={() => {
                            setErrorMessage("Utilisez l'Assistant pour générer un Master Shot ou glissez une image.");
                          }}
                        >
                          <span className="text-sm">Glisser ou générer via Assistant</span>
                        </div>
                      )}
                    </div>

                    {/* Vertical List (Shots Table) */}
                    <div className="flex-1 overflow-hidden">
                      <ShotsTable
                        shots={decoupageShots}
                        fps={decoupageFps}
                        onUpdateShot={handleUpdateDecoupageShot}
                        onDeleteShot={handleDeleteDecoupageShot}
                        onAddShot={handleAddDecoupageShot}
                        onDuplicateShot={handleDuplicateDecoupageShot}
                        onGenerateCandidates={handleGenerateShotCandidates}
                        onSelectShot={handleSelectDecoupageShot}
                        selectedShotId={selectedDecoupageShot?.id}
                        isGenerating={isGeneratingCandidates}
                        masterShot={decoupageMasterShot}
                        onRequestMasterShot={() => {
                          setAssistantMessages(prev => [...prev, {
                            role: 'user', content: "Génère moi un Plan d'Ensemble (Master Shot) pour cette scène."
                          }]);
                        }}
                        onClearMasterShot={() => setDecoupageMasterShot(null)}
                      />
                    </div>
                  </div>
                ) : promptSequence ? (
                  <SequenceManager
                    sequence={promptSequence}
                    activePromptIndex={activePromptIndex}
                    onSelectPrompt={handleSelectPromptFromSequence}
                    onClearSequence={handleClearSequence}
                    onEditRequest={handleEditPromptFromSequence}
                    revisingFromIndex={
                      isRevisingSequence?.fromIndex ?? null
                    }
                    videoData={sequenceVideoData}
                    storyboardByIndex={storyboardByIndex}
                    isGeneratingKeyframe={isGeneratingKeyframes}
                    generatingKeyframeIndex={generatingKeyframeIndex}
                    onThumbnailClick={async (thumbnailBase64, index) => {
                      const prompt = index === 0
                        ? promptSequence.mainPrompt
                        : promptSequence.extensionPrompts[index - 1] || '';
                      const existingImage = thumbnailBase64
                        ? { file: base64ToFile(thumbnailBase64, `keyframe_${index}.png`, 'image/png'), base64: thumbnailBase64 }
                        : storyboardByIndex[index]?.previewImage;
                      if (existingImage) {
                        openNanoEditor({ segmentIndex: index, baseImage: existingImage, initialPrompt: prompt });
                      } else {
                        console.log(`[ImageFirst] Generating preview for segment ${index}`);
                        setIsGeneratingKeyframes(true);
                        setGeneratingKeyframeIndex(index);
                        try {
                          const result = await generateNanoPreview({
                            textPrompt: prompt,
                            dogma: sequenceBoundDogma,
                            quality: 'pro',
                            target: index === 0 ? 'root' : 'extension',
                            apiKey: apiKey || undefined,
                            characters,
                          });
                          if (result.previewImage) {
                            setStoryboardByIndex(prev => ({
                              ...prev,
                              [index]: {
                                id: crypto.randomUUID(),
                                owner: index === 0 ? 'root' : 'extension',
                                previewImage: result.previewImage,
                                previewPrompt: result.previewPrompt || prompt,
                                segmentIndex: index,
                                cameraNotes: result.cameraNotes,
                                movementNotes: result.movementNotes,
                                createdAt: new Date().toISOString(),
                                updatedAt: new Date().toISOString(),
                              }
                            }));
                            console.log(`[ImageFirst] Preview generated for segment ${index}`);
                          }
                        } catch (err) {
                          console.error(`[ImageFirst] Failed to generate preview:`, err);
                          setErrorMessage(err instanceof Error ? err.message : 'Failed to generate preview.');
                        } finally {
                          setIsGeneratingKeyframes(false);
                          setGeneratingKeyframeIndex(null);
                        }
                      }
                    }}
                    onRegenerateKeyframe={async (segmentIndex) => {
                      console.log(`[ImageFirst] Regenerating keyframe for segment ${segmentIndex}`);
                      const prompt = segmentIndex === 0
                        ? promptSequence.mainPrompt
                        : promptSequence.extensionPrompts[segmentIndex - 1] || '';
                      setIsGeneratingKeyframes(true);
                      setGeneratingKeyframeIndex(segmentIndex);
                      try {
                        const result = await generateNanoPreview({
                          textPrompt: prompt,
                          dogma: sequenceBoundDogma,
                          quality: 'pro',
                          target: segmentIndex === 0 ? 'root' : 'extension',
                          apiKey: apiKey || undefined,
                          characters,
                        });
                        if (result.previewImage) {
                          setStoryboardByIndex(prev => ({
                            ...prev,
                            [segmentIndex]: {
                              id: crypto.randomUUID(),
                              owner: segmentIndex === 0 ? 'root' : 'extension',
                              previewImage: result.previewImage,
                              previewPrompt: result.previewPrompt || prompt,
                              segmentIndex,
                              cameraNotes: result.cameraNotes,
                              movementNotes: result.movementNotes,
                              createdAt: new Date().toISOString(),
                              updatedAt: new Date().toISOString(),
                            }
                          }));
                        }
                      } catch (err) {
                        console.error(`[ImageFirst] Regenerate failed:`, err);
                        setErrorMessage(err instanceof Error ? err.message : 'Failed to regenerate keyframe.');
                      } finally {
                        setIsGeneratingKeyframes(false);
                        setGeneratingKeyframeIndex(null);
                      }
                    }}
                    onUseKeyframeAsBase={(segmentIndex, image) => {
                      console.log(`[ImageFirst] Using keyframe ${segmentIndex} as base`);
                      const imageFile: ImageFile = {
                        file: base64ToFile(image.base64, `keyframe_${segmentIndex}.png`, 'image/png'),
                        base64: image.base64,
                      };
                      setAssistantImage(imageFile);
                      const prompt = segmentIndex === 0
                        ? promptSequence.mainPrompt
                        : promptSequence.extensionPrompts[segmentIndex - 1] || '';
                      setActivePromptIndex(segmentIndex);
                      setInitialFormValues(prev => prev ? { ...prev, prompt } : null);
                    }}
                  />
                ) : assistantImage ? (
                  <ModeSelectionStep
                    keyframeImage={assistantImage}
                    onSelectPlanSequence={() => setSequenceMode('plan-sequence')}
                    onSelectDecoupage={() => setSequenceMode('decoupage')}
                    isGenerating={isAssistantBusy}
                  />
                ) : (
                  <div className="h-full overflow-y-auto">
                    <PromptConception
                      apiKey={apiKey}
                      key={resetKey}
                      motionDescription={assistantMotionDescription}
                      referenceImage={assistantExtensionContext}
                      activeChatImage={assistantImage}
                      mentionedCharacters={mentionedCharacters}
                      sequenceHistory={sortedSequenceHistory}
                      onOpenNanoEditor={(segmentIndex, baseImage, initialPrompt) => {
                        openNanoEditor({ segmentIndex, baseImage, initialPrompt });
                      }}
                      promptSequence={promptSequence}
                      storyboardByIndex={storyboardByIndex}
                      isGeneratingKeyframes={isGeneratingKeyframes}
                      onUseAsStartImage={handleUseKeyframeAsStartImage}
                      onUseAsEndImage={handleUseKeyframeAsEndImage}
                      activeStartImageIndex={activeStartImageIndex}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {currentStage === AppStage.LOADING && (
          <div className="flex flex-col items-center justify-center h-full bg-[#121212]">
            <div className="flex flex-col items-center gap-6 p-8 bg-[#1a1a1c] border border-gray-800 rounded-2xl shadow-xl">
              <div className="relative">
                <div className="w-20 h-20 border-4 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" />
                <SparklesIcon className="w-8 h-8 text-indigo-400 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
              </div>
              <div className="text-center">
                <h3 className="text-xl font-bold text-white mb-2">Génération en cours...</h3>
                <p className="text-sm text-gray-400">
                  {lastConfig?.provider === VideoProvider.LTX
                    ? 'LTX-2 génère votre vidéo. Cela peut prendre 1-3 minutes.'
                    : 'Veo génère votre vidéo cinématique.'}
                </p>
              </div>
              {lastConfig?.prompt && (
                <div className="mt-2 px-4 py-2 bg-gray-800/50 rounded-lg max-w-md">
                  <p className="text-xs text-gray-500 italic line-clamp-2">"{lastConfig.prompt}"</p>
                </div>
              )}
            </div>
          </div>
        )}
        {currentStage === AppStage.RESULT && videoUrl && (
          <VideoResult
            videoUrl={videoUrl}
            lastConfig={lastConfig}
            onRetry={handleRetryLastPrompt}
            onStartNewProject={handleStartNewProject}
            onExtendVideo={handleExtendVideo}
            canExtend={lastConfig?.model !== VeoModel.VEO}
            onContinueFromFrame={handleContinueFromFrame}
            onEditCapturedFrame={handleEditCapturedFrame}
            sequenceProgress={sequenceProgress}
            onSaveShot={handleSaveShot}
            onContinueSequence={handleContinueSequence}
            originalVideoForExtension={originalVideoForExtension}
            onStartExtensionAssistant={handleStartExtensionAssistant}
            activeDogma={activeDogma}
            onPromptRevised={handlePromptRevised}
            onRecalNano={(segmentIndex, baseImage, initialPrompt) => {
              openNanoEditor({ segmentIndex, baseImage, initialPrompt });
            }}
            promptSequence={promptSequence}
            activePromptIndex={activePromptIndex}
            onVideoUploaded={(url, fileId) => {
              // Update persistent state when video is auto-uploaded
              if (activePromptIndex !== undefined && activePromptIndex !== null) {
                setSequenceVideoData(prev => ({
                  ...prev,
                  [activePromptIndex]: {
                    ...prev[activePromptIndex],
                    url: url,
                    driveFileId: fileId
                  }
                }));
                console.log('[Studio] Persisted Drive URL for segment', activePromptIndex);
              }
            }}
          />
        )}
        {currentStage === AppStage.EDITING && frameToEdit && (
          <div className="grid grid-cols-3 gap-4 h-full">
            <div className="col-span-2 h-full">
              <AIEditorModal
                image={frameToEdit}
                onClose={() => setCurrentStage(AppStage.RESULT)}
                onConfirm={handleFrameEditConfirm}
                dogma={activeDogma}
                apiKey={apiKey || undefined}
                characters={characters}
              />
            </div>
            <div className="col-span-1 h-full">
              <VisualContextViewer
                image={frameToEdit}
                stage={currentStage}
                onEditRequest={() => {
                  /* Already in editor */
                }}
                onApplyNano={handleApplyNano}
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    // <ProjectProvider> provided by App.tsx
    <ErrorBoundary>
      <div className={`h-screen w-screen bg-[#0f0f0f] text-white flex flex-row overflow-hidden transition-colors duration-300`}>
        {/* SIDE NAVIGATION */}
        <StudioSidebar
          activeTab={activeTab}
          onTabChange={setActiveTab}
          appState={appState}
          onSettingsClick={() => setShowApiKeyDialog(true)}
        />

        <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">
          <StudioLayout
            header={
              <StudioHeader
                activeProject={activeProject}
                onOpenProjectManager={() => setIsProjectManagerOpen(true)}
                appState={appState}
                onCancelGeneration={handleCancelGeneration}
                videoSettings={videoSettings}
                showStudioConfig={showStudioConfig}
                onToggleConfig={() => setShowStudioConfig(!showStudioConfig)}
                onOpenShotLibrary={() => setIsShotLibraryOpen(true)}
                onOpenCharacterManager={() => setIsCharacterManagerOpen(true)}
                onOpenDogmaManager={() => setIsDogmaManagerOpen(true)}
                activeDogma={activeDogma}
                onVideoSettingsChange={setVideoSettings}
              />
            }
            sidebarVisible={activeTab === 'editing'}
            sidebar={
              <aside className="w-80 h-full border-l border-[#333] bg-[#111] flex flex-col shrink-0 z-20" data-tour="bin-manager">
                <div className="flex-1 flex min-h-0 h-full">
                  <BinManager
                    projectShots={savedShots}
                    onMediaSelect={(media) => setSourceMedia(media)}
                    onAddToTimeline={handleAddToTimeline}
                  />
                </div>
              </aside>
            }
            toolbarVisible={false}
            toolbar={null}
            timelineVisible={activeTab === 'editing'}
            timeline={
              <>
                <HorizontalTimeline
                  // state prop removed
                  onPlayheadChange={handleSeek}
                  // onZoom removed
                  onSegmentTrim={handleSegmentTrim}
                  onSegmentMove={handleSegmentMove}
                  onSegmentClick={(id, shift) => {
                    // SWITCH FOCUS TO TIMELINE
                    setSourceMedia(null);

                    // Find segment to jump to its start
                    const segment = timelineState.segments.find(s => s.id === id);
                    if (segment) {
                      // Jump playhead to start of segment so user sees it
                      setTimelineState(prev => ({
                        ...prev,
                        selectedSegmentIds: [id],
                        playheadSec: segment.inSec + 0.01 // Small offset to ensure we are "in"
                      }));
                    } else {
                      const selectedIds = shift
                        ? timelineState.selectedSegmentIds.includes(id)
                          ? timelineState.selectedSegmentIds.filter(sid => sid !== id)
                          : [...timelineState.selectedSegmentIds, id]
                        : [id];
                      setTimelineState(prev => ({ ...prev, selectedSegmentIds: selectedIds }));
                    }
                  }}

                  onGapClick={(trackId, time, gapDur) => console.log('Gap click', trackId, time)}
                  selectedTrackId={timelineState.selectedTrackId}
                  selectedGap={null}
                  onTrackSelect={(id) => setTimelineState(prev => ({ ...prev, selectedTrackId: id }))}
                  tracks={timelineState.tracks}
                  segments={timelineState.segments}
                  selectedSegmentIds={timelineState.selectedSegmentIds}
                  playheadSec={timelineState.playheadSec}
                  fps={timelineState.project?.fps || 25}
                />
                <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileImport} multiple accept="video/*,image/*,audio/*" />
              </>
            }
            mainArea={
              <div className="h-full w-full relative">
                {/* LOADING / ERROR */}
                {appState === AppState.LOADING && activeTab === 'conception' && (
                  <div className="w-full h-full flex items-center justify-center">
                    <LoadingIndicator onCancel={handleCancelGeneration} />
                  </div>
                )}
                {appState === AppState.ERROR && (
                  <div className="w-full h-full flex items-center justify-center text-center">
                    <div>
                      <h2 className="text-2xl font-bold text-red-400 mb-4">An Error Occurred</h2>
                      <p className="text-gray-300 bg-red-900/50 p-4 rounded-lg max-w-lg mx-auto">{errorMessage}</p>
                      <button onClick={handleRetryLastPrompt} className="mt-6 px-5 py-2.5 bg-indigo-600 text-white rounded-lg">Try Again</button>
                    </div>
                  </div>
                )}

                {/* CONCEPTION */}
                {renderConceptionContent()}

                {/* EDITING */}
                <div className="flex w-full h-full" style={{ display: activeTab === 'editing' ? 'flex' : 'none' }}>
                  <div className="flex-1 flex flex-col relative min-w-0">
                    {sourceMedia ? (
                      <SourceViewer
                        media={sourceMedia}
                        onClose={() => setSourceMedia(null)}
                        onInsert={handleSourceInsert}
                        onOverwrite={handleSourceOverwrite}
                      />
                    ) : (
                      <TimelinePreview
                        tracks={timelineState.tracks}
                        segments={timelineState.segments}
                        playheadSec={timelineState.playheadSec}
                        fps={timelineState.project?.fps || 25}
                        isPlaying={isPlaying}
                        onPlayPause={handleTogglePlayback}
                        onSeek={handleSeek}
                      />
                    )}
                  </div>

                  {/* RIGHT SIDEBAR (Restored: Vertical Stack + IA Panel) */}
                  <aside className="w-[380px] flex flex-col shrink-0 border-l border-[#333] bg-[#1a1a1a] z-20">
                    {/* 1. TOP: Vertical Timeline Stack */}
                    <div className="flex-1 flex flex-col min-h-0 border-b border-[#3f3f46]">
                      <div className="h-10 border-b border-[#3f3f46] flex items-center justify-between px-3 bg-[#1a1a1a] shrink-0">
                        <div className="flex items-center gap-2">
                          <span className="material-symbols-outlined text-gray-400 text-sm">view_timeline</span>
                          <span className="font-semibold text-[10px] text-gray-200 uppercase tracking-widest">Vertical Timeline Stack</span>
                        </div>
                      </div>
                      <div className="flex-1 overflow-y-auto p-2 bg-[#121212]">
                        <VerticalTimelineStack
                          segments={timelineState.segments}
                          selectedSegmentIds={timelineState.selectedSegmentIds}
                          expandedSegmentIds={timelineState.expandedSegmentIds}
                          onSegmentClick={(id) => {
                            setSourceMedia(null); // Switch focus to timeline
                            setTimelineState(s => ({ ...s, selectedSegmentIds: [id] }));
                          }}
                          onSegmentExpand={(id) => setTimelineState(s => ({ ...s, expandedSegmentIds: [...s.expandedSegmentIds, id] }))}
                          onSegmentCollapse={(id) => setTimelineState(s => ({ ...s, expandedSegmentIds: s.expandedSegmentIds.filter(x => x !== id) }))}
                          onIterationClick={() => { }}
                          onIterationValidate={() => { }}
                          onIterationDelete={() => { }}
                          onSegmentLock={() => { }}
                          onSegmentUnlock={() => { }}
                          onReprompt={() => { }}
                          onReorder={() => { }}
                          onSegmentDelete={handleDeleteShot} // Map to library delete or need specific timeline delete? Using generic for now
                          onSegmentDuplicate={() => { }}
                        />
                      </div>
                    </div>

                    {/* 2. BOTTOM: IA Panel */}
                    <div className="h-[380px] bg-[#2a2a2a] flex flex-col shrink-0 shadow-[0_-4px_20px_rgba(0,0,0,0.3)] z-10">
                      <SegmentIAPanel
                        activeTab={iaPanelTab}
                        onTabChange={setIaPanelTab}
                        segment={timelineState.segments.find(s => timelineState.selectedSegmentIds.includes(s.id)) || null}
                        activeRevision={null}
                        onReprompt={() => { }}
                        onRegenerate={() => { }}
                      />
                    </div>
                  </aside>
                </div>


                {/* EXPORT */}
                <div className="w-full h-full p-12 flex flex-col items-center justify-center text-center bg-[#121212]" style={{ display: activeTab === 'export' ? 'flex' : 'none' }}>
                  <div className="max-w-lg w-full bg-[#1a1a1a] border border-gray-800 p-12 rounded-[2.5rem] shadow-[0_32px_64px_-12px_rgba(0,0,0,0.5)]">
                    <div className="w-24 h-24 bg-indigo-600/10 rounded-3xl flex items-center justify-center mx-auto mb-10 rotate-3 hover:rotate-0 transition-transform duration-500">
                      <UploadCloudIcon className="w-12 h-12 text-indigo-500" />
                    </div>
                    <h2 className="text-3xl font-bold text-white mb-4">Prêt pour l'Export ?</h2>
                    <p className="text-gray-500 mb-12">Finalisez votre montage et exportez.</p>
                    <div className="grid gap-4">
                      <button onClick={() => setIsExportDialogOpen(true)} className="w-full py-5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-3xl font-bold text-lg flex items-center justify-center gap-3 group">
                        <SparklesIcon className="w-6 h-6 group-hover:animate-sparkle" /><span>Exporter le MP4</span>
                      </button>
                      <button onClick={handleSaveProjectJson} className="w-full py-3 bg-gray-800 text-gray-300 rounded-3xl">Sauvegarder le Projet</button>
                    </div>
                  </div>
                </div>
              </div>
            }
          />
        </div>


        {/* ONBOARDING GUIDE - RESTORED */}
        <OnboardingGuide
          key={onboardingKey}
          isProjectActive={!!activeProject}
          isSettingsOpen={showApiKeyDialog}
          activeSettingsTab={activeSettingsTab}
          activeTab={activeTab}
          apiKeyPresent={!!apiKey}
          falKeyPresent={!!getProviderKey(ProviderKeys.FAL_LTX)}
          onCloseSettings={() => setShowApiKeyDialog(false)}
          onSetTab={setActiveTab}
        />

        {/* ===================================================================================== */}
        {/* MODALS LAYER - MOVED TO ROOT TO PREVENT CLIPPING & Z-INDEX ISSUES                     */}
        {/* ===================================================================================== */}

        {/* SETTINGS / USER PROFILE MODAL */}
        <UserProfileModal
          isOpen={showApiKeyDialog}
          onClose={() => setShowApiKeyDialog(false)}
          apiKey={apiKey}
          onSetKey={handleSetApiKey}
          onClearKey={() => {
            setApiKey(null);
            setRuntimeApiKey(null);
            setHasCustomKey(false);
          }}
          activeTab={activeSettingsTab}
          onTabChange={setActiveSettingsTab}
          onResetOnboarding={() => {
            localStorage.removeItem('studio_jenial_onboarding_seen_v2');
            window.location.reload();
          }}
        />
        {/* SHOT LIBRARY */}
        {isShotLibraryOpen && (
          <ShotLibrary
            isOpen={isShotLibraryOpen}
            shots={savedShots}
            onClose={() => setIsShotLibraryOpen(false)}
            onLoadShot={handleLoadShot}
            onDeleteShot={handleDeleteShot}
            onUpdateShotTitle={handleUpdateShotTitle}
          />
        )}

        {/* PROJECT MANAGER */}
        <ProjectManager
          isOpen={isProjectManagerOpen}
          onClose={() => setIsProjectManagerOpen(false)}
          onProjectChange={() => {
            // Clear project-scoped state when switching/creating projects
            setStoryboardByIndex({});
            setPromptSequence(null);
            setSequenceVideoData({});
            setMentionedCharacters([]);
            setAssistantImage(null);
            setAssistantReferenceVideo(null);
            setAssistantMotionDescription(null);
            setAssistantExtensionContext(null);
            setOriginalVideoForExtension(null);
            setIsExternalVideoSource(false);
            setCharacterToInject(null);
            setEditingPromptDetails(null);
            setNanoEditorContext(null);
            setActivePromptIndex(null);
            setDecoupageShots([]);
            setSelectedDecoupageShot(null);
            setCandidatesByShotId({});
            setLastVideoBlob(null);
            setLastVideoObject(null);
            setVideoUrl(null);
          }}
        />

        {/* CHARACTER MANAGER */}
        {isCharacterManagerOpen && (
          <CharacterManagerModal
            isOpen={isCharacterManagerOpen}
            characters={characters}
            onSaveCharacter={(character) => {
              if (character.id) {
                handleUpdateCharacter(character as Character);
              } else {
                const newChar: Character = { ...character, id: crypto.randomUUID() } as Character;
                handleAddCharacter(newChar);
              }
            }}
            onDeleteCharacter={handleDeleteCharacter}
            onClose={() => setIsCharacterManagerOpen(false)}
            onUseCharacter={setCharacterToInject}
            folders={folders}
            onCreateFolder={createFolder}
            onDeleteFolder={handleDeleteFolder}
          />
        )}

        {/* DOGMA MANAGER */}
        {isDogmaManagerOpen && (
          <DogmaManagerModal
            isOpen={isDogmaManagerOpen}
            dogmas={dogmas}
            onSaveDogma={(dogma) => {
              if (dogma.id) {
                setDogmas(prev => prev.map(d => d.id === dogma.id ? { ...d, ...dogma } as Dogma : d));
              } else {
                const newDogma: Dogma = { ...dogma, id: crypto.randomUUID() } as Dogma;
                setDogmas(prev => [...prev, newDogma]);
              }
            }}
            onDeleteDogma={(dogmaId) => {
              setDogmas(prev => prev.filter(d => d.id !== dogmaId));
              if (activeDogmaId === dogmaId) setActiveDogmaId(null);
            }}
            activeDogmaId={activeDogmaId}
            pendingDogmaId={pendingDogma?.id || null}
            onSetActiveDogmaId={handleActivateDogmaBuffered}
            onClose={() => setIsDogmaManagerOpen(false)}
          />
        )}

        {/* EXPORT DIALOG */}
        {isExportDialogOpen && (
          <ExportDialog
            isOpen={isExportDialogOpen}
            onClose={() => setIsExportDialogOpen(false)}
            segments={timelineState.segments}
          />
        )}

        {/* PROJECT WELCOME MODAL - Self-managing: shows only when no project active */}
        <ProjectWelcomeModal onOpenSettings={() => {
          setActiveSettingsTab('integrations');
          setShowApiKeyDialog(true);
        }} />

        {/* CONFIG DIALOG - REMOVED (Logic handled by PromptSequenceAssistant via showStudioConfig prop) */}

        {/* STORYBOARD PREVIEW MODAL (12 Grid Shots) */}
        {storyboardModalContext && (
          <StoryboardPreviewModal
            isOpen={true}
            onClose={() => setStoryboardModalContext(null)}
            onApplyVariant={(payload: NanoApplyPayload) => {
              // Apply single variant to storyboard
              if (payload.previewImage) {
                setStoryboardByIndex(prev => ({
                  ...prev,
                  [storyboardModalContext.segmentIndex]: {
                    id: crypto.randomUUID(),
                    owner: 'root',
                    segmentIndex: storyboardModalContext.segmentIndex,
                    previewImage: payload.previewImage,
                    previewPrompt: payload.previewPrompt || storyboardModalContext.currentPrompt,
                    cameraNotes: payload.cameraNotes,
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                  }
                }));
              }
              setStoryboardModalContext(null);
            }}
            segmentIndex={storyboardModalContext.segmentIndex}
            baseImage={storyboardModalContext.baseImage}
            currentPrompt={storyboardModalContext.currentPrompt}
            dogma={sequenceBoundDogma ?? activeDogma}
            mode={storyboardModalContext.mode}
            onBuildTimeline={(shots) => {
              // Create a sequence bin from ordered shots
              const binInput: CreateSequenceBinInput = {
                name: `Découpage - ${new Date().toLocaleTimeString()}`,
                dogma: sequenceBoundDogma ?? activeDogma,
                rootPrompt: storyboardModalContext.currentPrompt,
                shots: shots.map(shot => ({
                  shotType: shot.shotType,
                  prompt: shot.prompt,
                  duration: shot.duration,
                  cameraMovement: shot.cameraMovement,
                  keyframe: shot.image,
                })),
              };
              const newBin = createBin(binInput);
              console.log('[Studio] Created sequence bin:', newBin.name);
              setStoryboardModalContext(null);
            }}
            apiKey={apiKey || undefined}
          />
        )}

        {/* PROMPT EDITOR MODAL (Revision Chat) */}
        {editingPromptDetails && (
          <PromptEditorModal
            originalPrompt={editingPromptDetails.prompt}
            visualContextBase64={editingPromptDetails.thumbnail}
            onClose={() => setEditingPromptDetails(null)}
            onConfirm={(newPrompt: string) => {
              if (promptSequence && editingPromptDetails) {
                const updated = { ...promptSequence };
                if (editingPromptDetails.index === 0) {
                  updated.mainPrompt = newPrompt;
                } else if (updated.extensionPrompts?.[editingPromptDetails.index - 1] !== undefined) {
                  updated.extensionPrompts[editingPromptDetails.index - 1] = newPrompt;
                }
                setPromptSequence(updated);
              }
              setEditingPromptDetails(null);
            }}
            dogma={sequenceBoundDogma ?? activeDogma}
            characters={characters}
            onOpenNanoEditor={() => {
              // Open Nano editor with current context
              if (editingPromptDetails) {
                const keyframe = storyboardByIndex[editingPromptDetails.index];
                if (keyframe) {
                  openNanoEditor({
                    segmentIndex: editingPromptDetails.index,
                    baseImage: keyframe.previewImage,
                    initialPrompt: editingPromptDetails.prompt,
                  });
                }
              }
              setEditingPromptDetails(null);
            }}
          />
        )}

        {/* THEME MODAL */}
        {showThemeModal && (
          <ThemeSwitcher variant="full" onClose={() => setShowThemeModal(false)} />
        )}

        {/* NANO PRO EDITOR BLOQUÉ FIX */}
        {nanoEditorContext && (
          <AIEditorModal
            image={nanoEditorContext.baseImage!}
            onClose={closeNanoEditor}
            onConfirm={(newImage) => {
              handleNanoApply({
                target: nanoEditorContext.target,
                segmentIndex: nanoEditorContext.segmentIndex,
                previewImage: newImage,
                previewPrompt: nanoEditorContext.initialPrompt || '',
              });
            }}
            dogma={nanoEditorContext.dogma}
            initialPrompt={nanoEditorContext.initialPrompt}
            apiKey={apiKey || undefined}
          />
        )}

        {/* VIDEO FRAME SELECTOR FOR INFINITE CHAIN */}
        {chainFrameSelectorOpen && (
          <VideoFrameSelectorModal
            isOpen={true}
            onClose={() => setChainFrameSelectorOpen(null)}
            onConfirm={(image) => {
              const { segmentId, targetImage } = chainFrameSelectorOpen;
              setChainSegments(prev => prev.map(seg => {
                if (seg.id === segmentId) {
                  const updated = targetImage === 'start'
                    ? { ...seg, startImage: image }
                    : { ...seg, endImage: image };
                  // Check if segment is now ready
                  if (updated.startImage && updated.endImage && updated.actionPrompt.trim()) {
                    updated.status = 'ready';
                  }
                  return updated;
                }
                return seg;
              }));
              setChainFrameSelectorOpen(null);
            }}
          />
        )}
      </div>
    </ErrorBoundary>
  );
};

// Local Interface Definition (Missing from types)
interface RushMedia {
  id: string;
  name: string;
  type: string;
  durationSec?: number;
  metadata?: { fps?: number; totalFrames?: number };
  localUrl?: string;
  thumbnail?: string;
}

export default Studio;
